/**
 * Live TV card forms: Currently On Air + Missed Auctions.
 * - COA: .currently-on-air-card__form (card & View Details popup), GLA/MLA, buy-all, qty, read-more.
 * - Missed: .missed-auction-card, GLA/MLA, buy-all, qty, gallery.
 * Shared: select validation (invalid-selection), document-level change/click listeners.
 * Call window.initCoaCardForms() / window.initMissedAuctionForms() after dynamically adding cards.
 */
(function () {
  var LTV_BP_DEBUG_KEY = "ltv_bp_debug";

  function isLtvBpDebugEnabled() {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage &&
        window.localStorage.getItem(LTV_BP_DEBUG_KEY) === "1"
      );
    } catch (e) {
      return false;
    }
  }

  function ltvBpDebugLog(label, detail) {
    if (!isLtvBpDebugEnabled()) return;
    if (detail !== undefined)
      console.log("[BudgetPay][LTV][forms] " + label, detail);
    else console.log("[BudgetPay][LTV][forms] " + label);
  }

  /**
   * Budget Pay toggle + selling_plan input state for diagnostics at add-to-cart.
   * @param {HTMLFormElement|null} form
   */
  function getCoaBudgetPayDiagnostics(form) {
    var article = form && form.closest("article.currently-on-air-card");
    var toggle = article
      ? article.querySelector("[data-budget-pay-toggle]")
      : null;
    var inputId =
      article && article.getAttribute("data-bp-selling-plan-input-id");
    var byId = inputId ? document.getElementById(inputId) : null;
    var byName = form ? form.querySelector('input[name="selling_plan"]') : null;
    var inst = toggle && toggle._budgetPayToggleInstance;
    return {
      hasArticle: !!article,
      inputId: inputId || null,
      toggleDataActive: !!(toggle && toggle.hasAttribute("data-active")),
      toggleInstanceActive: !!(inst && inst.active),
      sellingPlanById: byId ? String(byId.value || "") : null,
      sellingPlanByIdInForm: !!(byId && form && form.contains(byId)),
      sellingPlanByName: byName ? String(byName.value || "") : null,
      bpMin: form
        ? (
            form.querySelector(
              'input[name="properties[_bp_min_installments]"]',
            ) || {}
          ).value
        : null,
    };
  }

  function isInvalidSelection(select) {
    if (!select) return true;
    if (!select.options || select.options.length === 0) return true;
    var idx = select.selectedIndex;
    if (idx < 0) return true;
    if (idx > 0) return false;
    var opt = select.options[idx];
    var v =
      opt && opt.value != null
        ? String(opt.value).trim()
        : (select.value || "").trim();
    return v === "" || v === "Select";
  }

  /** GLA/MLA or single variant dropdown — same rules as Add to bag (Shop Pay / wallets use shadow DOM, see payment click handler). */
  function validateGlaMlaOrVariantForm(form) {
    if (!form) return true;
    var glaMlaRow = form.querySelector("[data-gla-mla-form]");
    var glaSelect = form.querySelector("[data-gla-select]");
    var mlaSelect = form.querySelector("[data-mla-select]");
    var variantSelect = form.querySelector("[data-variant-select]");
    if (glaMlaRow && glaSelect && mlaSelect) {
      if (isInvalidSelection(glaSelect)) {
        glaSelect.classList.add("invalid-selection");
        glaSelect.focus();
        return false;
      }
      if (!mlaSelect.disabled && isInvalidSelection(mlaSelect)) {
        mlaSelect.classList.add("invalid-selection");
        mlaSelect.focus();
        return false;
      }
    } else if (variantSelect && isInvalidSelection(variantSelect)) {
      variantSelect.classList.add("invalid-selection");
      variantSelect.focus();
      return false;
    }
    return true;
  }

  function readInvBySkuFromCard(card) {
    if (!card || !card.getAttribute) return null;
    var raw = card.getAttribute("data-shopify-inventory");
    if (!raw) return null;
    try {
      var o = JSON.parse(String(raw).replace(/&quot;/g, '"'));
      return o && o.inventoryBySku && typeof o.inventoryBySku === "object"
        ? o.inventoryBySku
        : null;
    } catch (_) {
      return null;
    }
  }

  function skuSoldOut(inv, sku) {
    if (!inv || !sku) return false;
    var ent = inv[sku] || inv[String(sku).trim()];
    return !!(ent && ent.availableForSale === false);
  }

  function skuStorefrontUnavailable(inv, sku) {
    if (!inv || !sku) return false;
    var ent = inv[sku] || inv[String(sku).trim()];
    return !!(ent && ent.storefrontAvailable === false);
  }

  /* COA cards render with a Liquid snapshot of data-shopify-inventory (tab cards get theirs
     from the API); fold each sku-data / prefetch response into the card's map so it stays
     live for syncLiveTvAddButtonState and dropdown rebuilds to gate on. */
  function mergeSkuAvailabilityIntoCard(
    form,
    sku,
    availableForSale,
    storefrontAvailable,
  ) {
    if (!form || !sku || typeof availableForSale !== "boolean") return;
    var card = form.closest(
      ".missed-auction-card, .currently-on-air-card, article[data-product-id]",
    );
    if (!card) return;
    var inv = readInvBySkuFromCard(card) || {};
    var key = String(sku).trim();
    var ent = inv[key] || inv[sku] || {};
    ent.availableForSale = availableForSale;
    if (typeof storefrontAvailable === "boolean")
      ent.storefrontAvailable = storefrontAvailable;
    inv[key] = ent;
    try {
      card.setAttribute(
        "data-shopify-inventory",
        JSON.stringify({ inventoryBySku: inv }).replace(/"/g, "&quot;"),
      );
    } catch (_) {}
  }

  function liveTvVariantSoldOutSuffix(form) {
    var own =
      form && form.getAttribute
        ? form.getAttribute("data-variant-sold-out-suffix")
        : "";
    if (own) return own;
    var grid =
      form && form.closest ? form.closest("[data-tab-items-grid]") : null;
    return (
      (grid && grid.getAttribute("data-variant-sold-out-suffix")) ||
      " (sold out)"
    );
  }

  /* Sold out must be visible in the option label itself — the add-button flip alone only shows
     after the shopper has already picked the option. Ring-resize sizes are governed by the
     master sku and buy-all is a bundle row, so neither is marked from its own sku. */
  function markLiveTvOptionSoldOut(form, opt, availableForSale) {
    if (!form || !opt || typeof availableForSale !== "boolean") return;
    if (
      opt.getAttribute("data-ring-resize") === "true" ||
      opt.getAttribute("data-buy-all") === "true"
    )
      return;
    var suffix = liveTvVariantSoldOutSuffix(form);
    var label = opt.textContent || "";
    if (!availableForSale) {
      if (label.indexOf(suffix) === -1) opt.textContent = label + suffix;
      opt.disabled = true;
    } else if (label.indexOf(suffix) !== -1) {
      opt.textContent = label.split(suffix).join("");
      opt.disabled = false;
    }
  }

  function applyLiveTvAvailabilityToFormOptions(form, availBySku) {
    if (!form || !availBySku) return;
    var opts = form.querySelectorAll("option[data-sku]");
    for (var i = 0; i < opts.length; i++) {
      var s = opts[i].getAttribute("data-sku");
      var afs = s != null ? availBySku[String(s).trim()] : undefined;
      if (typeof afs === "boolean") markLiveTvOptionSoldOut(form, opts[i], afs);
    }
    syncLiveTvAddButtonState(form);
  }

  /* One round trip for a set of skus (buy-all-variants shares the sku-data Redis cache):
     merge availability into the card map — dropdown rebuilds read it synchronously, so
     options built after this resolve show "(sold out)" instantly — and mark any options
     already in the DOM. */
  function prefetchLiveTvSkuAvailability(form, skus) {
    if (!form || !skus || skus.length === 0) return;
    var grid = form.closest ? form.closest("[data-tab-api-url]") : null;
    var apiBase = grid
      ? (grid.getAttribute("data-tab-api-url") || "").replace(/\/[^/]+$/, "")
      : "";
    if (!apiBase) apiBase = "/apps/live-tv";
    fetch(
      apiBase + "/buy-all-variants?skus=" + encodeURIComponent(skus.join(",")),
      {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    )
      .then(function (r) {
        return r.ok ? r.json() : Promise.reject();
      })
      .then(function (json) {
        var variants =
          json && json.success && json.data && Array.isArray(json.data.variants)
            ? json.data.variants
            : null;
        if (!variants) return;
        var availBySku = {};
        for (var v = 0; v < variants.length; v++) {
          var ent = variants[v];
          if (!ent || !ent.sku || typeof ent.availableForSale !== "boolean")
            continue;
          var key = String(ent.sku).trim();
          availBySku[key] = ent.availableForSale;
          mergeSkuAvailabilityIntoCard(
            form,
            key,
            ent.availableForSale,
            ent.storefrontAvailable,
          );
        }
        applyLiveTvAvailabilityToFormOptions(form, availBySku);
      })
      .catch(function () {});
  }

  /* Refresh availability for the skus already present as options in one select. */
  function prefetchLiveTvOptionAvailability(form, select) {
    if (!form || !select) return;
    var skus = [];
    for (var i = 0; i < select.options.length; i++) {
      var o = select.options[i];
      var sku = o.getAttribute && o.getAttribute("data-sku");
      if (!sku) continue;
      if (
        o.getAttribute("data-ring-resize") === "true" ||
        o.getAttribute("data-buy-all") === "true"
      )
        continue;
      if (skus.indexOf(sku) === -1) skus.push(sku);
    }
    prefetchLiveTvSkuAvailability(form, skus);
  }

  /* app.js's initCardForm owns the GLA/MLA listeners when both scripts are loaded — expose the
     availability plumbing so its sku-data callbacks can gate the add button too. */
  window.liveTvMergeSkuAvailability = mergeSkuAvailabilityIntoCard;
  window.liveTvSyncAddButtonState = syncLiveTvAddButtonState;
  window.liveTvMarkOptionSoldOut = markLiveTvOptionSoldOut;
  window.liveTvPrefetchOptionAvailability = prefetchLiveTvOptionAvailability;
  /* Same reason for the ring-resize stamp: app.js inits the tab cards first (and marks them
     inited), so initMissedAuctionCard's own select handler never attaches and the
     _isRingResize / _resize* properties would never reach the cart line. */
  window.liveTvApplyRingResize = applyRingResizeFromSelectedOption;

  function getLiveTvAddButtonLabels(form) {
    return {
      add: form.getAttribute("data-label-add") || "Add To Bag",
      soldOut: form.getAttribute("data-label-sold-out") || "Sold out",
    };
  }

  /** Sync Add to Basket disabled state + label from selected variant / inventory. */
  function syncLiveTvAddButtonState(form) {
    if (!form) return;
    var btn = form.querySelector('button[type="submit"][name="add"]');
    if (!btn) return;
    var label = btn.querySelector(
      ".currently-on-air-card__cta-label, .missed-auction-card__cta-label",
    );
    if (!label) return;
    var labels = getLiveTvAddButtonLabels(form);
    if (!btn.dataset.ltvDefaultAddLabel)
      btn.dataset.ltvDefaultAddLabel = labels.add;

    /* Shopify Admin still returns full details for Draft products, but the storefront cart
       rejects their variants. Keep the requested Add label while preventing a request that
       can only fail. */
    var storefrontUnavailable =
      form.getAttribute("data-storefront-unavailable") === "true";
    var storefrontVariantSelect = form.querySelector("[data-variant-select]");
    var storefrontBuyAllRow = form.querySelector("[data-buy-all-skus]");
    if (
      storefrontVariantSelect &&
      storefrontVariantSelect.value === "buy-all" &&
      storefrontBuyAllRow &&
      storefrontBuyAllRow.getAttribute(
        "data-buy-all-storefront-unavailable",
      ) === "true"
    ) {
      storefrontUnavailable = true;
    }
    if (!storefrontUnavailable) {
      var storefrontSelect =
        form.querySelector("[data-mla-select]:not(:disabled)") ||
        form.querySelector("[data-variant-select]");
      var storefrontOpt =
        storefrontSelect &&
        storefrontSelect.options[storefrontSelect.selectedIndex];
      var storefrontSku =
        storefrontOpt && storefrontOpt.getAttribute("data-sku");
      if (!storefrontSku) {
        var storefrontGla = form.querySelector("[data-gla-select]");
        var storefrontGlaOpt =
          storefrontGla && storefrontGla.options[storefrontGla.selectedIndex];
        storefrontSku =
          storefrontGlaOpt && storefrontGlaOpt.getAttribute("data-stock-code");
      }
      var storefrontCard = form.closest(
        ".missed-auction-card, .currently-on-air-card, article[data-product-id]",
      );
      storefrontUnavailable = !!(
        storefrontSku &&
        skuStorefrontUnavailable(
          readInvBySkuFromCard(storefrontCard),
          storefrontSku,
        )
      );
    }

    var variantSel = form.querySelector("[data-variant-select]");
    var mlaSel = form.querySelector("[data-mla-select]");
    var soldOut = null;

    /* Ring resize: the master variant is the physical ring — its availability governs every size.
       Size variants carry no stock of their own, so never gate on the picked size's SKU. */
    var rrRow = form.querySelector("[data-ring-resize-form]");
    if (rrRow) {
      if (rrRow.getAttribute("data-ring-resize-master-available") === "false") {
        soldOut = true;
      } else {
        /* Rebuilt COA options can carry data-sku with an empty value (variant id resolved later via sku-data),
           so a selection counts when either is present. */
        var rrOpt = variantSel
          ? variantSel.options[variantSel.selectedIndex]
          : null;
        var rrPicked =
          !variantSel ||
          !!(variantSel.value || (rrOpt && rrOpt.getAttribute("data-sku")));
        if (!rrPicked) {
          soldOut = null;
        } else {
          var rrMasterSku = rrRow.getAttribute("data-ring-resize-master-sku");
          var rrCard = form.closest(
            ".missed-auction-card, .currently-on-air-card, article[data-product-id]",
          );
          soldOut = !!(
            rrMasterSku && skuSoldOut(readInvBySkuFromCard(rrCard), rrMasterSku)
          );
        }
      }
    } else if (mlaSel && !mlaSel.disabled) {
      var mlaOpt = mlaSel.options[mlaSel.selectedIndex];
      var mlaSku = mlaOpt && mlaOpt.getAttribute("data-sku");
      if (!mlaSel.value && !mlaSku) {
        soldOut = null;
      } else if (mlaOpt && mlaOpt.disabled) {
        soldOut = true;
      } else {
        var cardMla = form.closest(
          ".missed-auction-card, .currently-on-air-card, article[data-product-id]",
        );
        soldOut = !!(
          mlaSku && skuSoldOut(readInvBySkuFromCard(cardMla), mlaSku)
        );
      }
    } else if (variantSel) {
      var vOpt = variantSel.options[variantSel.selectedIndex];
      var vSku = vOpt && vOpt.getAttribute("data-sku");
      if (!variantSel.value && !vSku) {
        soldOut = null;
      } else if (variantSel.value === "buy-all") {
        var buyAllStateRow = form.querySelector("[data-buy-all-skus]");
        var buyAllIdsAttr =
          buyAllStateRow &&
          buyAllStateRow.getAttribute("data-buy-all-variant-ids");
        /* Cards whose flow never writes this attribute stay enabled as before. Where the flow
           does write it, it is cleared before the request, so an empty value means either the
           request is still pending or every candidate is OOS / Draft. */
        soldOut =
          typeof buyAllIdsAttr === "string" &&
          buyAllIdsAttr.split(",").filter(Boolean).length === 0;
      } else if (vOpt && vOpt.disabled) {
        soldOut = true;
      } else {
        var cardVar = form.closest(
          ".missed-auction-card, .currently-on-air-card, article[data-product-id]",
        );
        soldOut = !!(vSku && skuSoldOut(readInvBySkuFromCard(cardVar), vSku));
      }
    } else if (form.getAttribute("data-initial-sold-out") === "true") {
      soldOut = true;
    } else if (
      form.classList.contains("currently-on-air-card__form") &&
      btn.disabled &&
      label.textContent.trim() === labels.soldOut
    ) {
      soldOut = true;
    } else {
      var cardSimple = form.closest(
        ".missed-auction-card, article[data-product-id]",
      );
      var simpleSku = cardSimple && cardSimple.getAttribute("data-product-sku");
      soldOut = !!(
        simpleSku && skuSoldOut(readInvBySkuFromCard(cardSimple), simpleSku)
      );
    }

    /* Sold out is decided first so a genuinely OOS item keeps the "Sold out" label; being off
       the storefront only adds a disable on top, keeping the Add label as requested. */
    if (soldOut === null) {
      /* ltvAddLoading also covers a Quick Buy in flight, whose spinner sits on the other button. */
      if (!btn.classList.contains("is-loading") && btn.dataset.ltvAddLoading !== "1") {
        btn.disabled = storefrontUnavailable;
        label.textContent = btn.dataset.ltvDefaultAddLabel || labels.add;
      }
      return;
    }
    if (soldOut) {
      btn.disabled = true;
      label.textContent = labels.soldOut;
    } else if (btn.dataset.ltvAddLoading !== "1") {
      btn.disabled = storefrontUnavailable;
      label.textContent = btn.dataset.ltvDefaultAddLabel || labels.add;
    }
  }

  /** Placeholder markup when app.js helpers unavailable (same URL source as app.js). */
  function liveTvNoImageUrlFromDom() {
    var el = document.querySelector("[data-ltv-no-img]");
    return el ? el.getAttribute("data-ltv-no-img") : "";
  }

  /* app.js owns these (see the "Card images" block there). This file is loaded on pages
     where app.js is present too, but it must not break if that ever changes, so each
     helper falls back to returning the URL untouched — the same pattern the placeholder
     helpers below already use. */
  var LTV_CARD_IMG_WIDTH = 400;
  var LTV_COA_IMG_WIDTH = 200;
  var LTV_CARD_IMG_SIZES = "(max-width: 749px) 45vw, 342px";

  function ltvImgUrl(url, width) {
    if (typeof window.ltvImgUrl === "function")
      return window.ltvImgUrl(url, width);
    return url || "";
  }

  function ltvImgTag(url, opts) {
    if (typeof window.ltvImgTag === "function")
      return window.ltvImgTag(url, opts);
    opts = opts || {};
    return (
      '<img src="' +
      String(url || "").replace(/"/g, "&quot;") +
      '" alt="" width="' +
      (opts.boxWidth || 342) +
      '" height="' +
      (opts.boxHeight || 342) +
      '" loading="lazy" decoding="async" class="' +
      String(opts.className || "") +
      '"' +
      (opts.extra ? " " + opts.extra : "") +
      " />"
    );
  }

  function ltvSetImgSrc(imgEl, url, width) {
    if (typeof window.ltvSetImgSrc === "function")
      return window.ltvSetImgSrc(imgEl, url, width);
    if (imgEl && imgEl.tagName === "IMG") imgEl.src = url || "";
  }

  function missedAuctionPlaceholderHtml() {
    if (typeof window.liveTvMissedPlaceholderHtml === "function") {
      return window.liveTvMissedPlaceholderHtml();
    }
    var url = liveTvNoImageUrlFromDom();
    if (!url) {
      return '<div class="missed-auction-card__img missed-auction-card__img--placeholder" aria-hidden="true"></div>';
    }
    return (
      '<img src="' +
      String(url).replace(/"/g, "&quot;") +
      '" alt="" width="342" height="342" loading="lazy" class="missed-auction-card__img missed-auction-card__img--placeholder" aria-hidden="true" />'
    );
  }

  function coaPlaceholderHtml() {
    if (typeof window.liveTvCoaPlaceholderHtml === "function") {
      return window.liveTvCoaPlaceholderHtml();
    }
    var url = liveTvNoImageUrlFromDom();
    if (!url) {
      return '<div class="currently-on-air-card__img currently-on-air-card__img--placeholder" aria-hidden="true"></div>';
    }
    return (
      '<img src="' +
      String(url).replace(/"/g, "&quot;") +
      '" alt="" width="150" height="150" loading="lazy" class="currently-on-air-card__img currently-on-air-card__img--placeholder" aria-hidden="true" />'
    );
  }

  function coaFormFromPaymentClick(e) {
    var path = typeof e.composedPath === "function" ? e.composedPath() : [];
    var i;
    for (i = 0; i < path.length; i++) {
      var n = path[i];
      if (
        n &&
        n.nodeName === "FORM" &&
        n.classList &&
        n.classList.contains("currently-on-air-card__form")
      )
        return n;
    }
    return null;
  }

  function clickIsInsideCoaPaymentButtons(e) {
    var path = typeof e.composedPath === "function" ? e.composedPath() : [];
    var k;
    for (k = 0; k < path.length; k++) {
      var p = path[k];
      if (
        p &&
        p.classList &&
        p.classList.contains("currently-on-air-card__payment-buttons")
      )
        return true;
    }
    return !!(
      e.target &&
      e.target.closest &&
      e.target.closest(".currently-on-air-card__payment-buttons")
    );
  }

  function formatNumber(num, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? "," : thousands;
    decimal = decimal == null ? "." : decimal;
    if (num == null || isNaN(Number(num))) return "";
    var fixed = Number(num).toFixed(precision);
    var parts = fixed.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (precision === 0) return parts[0];
    return parts[0] + decimal + parts[1];
  }

  /**
   * The auction's own price for the SKU behind an option (`data-line-price`, stamped from
   * groupAuctions/mlaAuctions currentPrice). Group lines routinely differ from the main lot's
   * price, and neither the main live price nor the Shopify catalogue price from sku-data can
   * stand in for them.
   * @returns {number|null} null when the option carries no usable line price
   */
  function liveTvLinePriceOf(opt) {
    if (!opt || !opt.getAttribute) return null;
    var raw = opt.getAttribute("data-line-price");
    if (raw == null || String(raw).trim() === "") return null;
    var n = Number(raw);
    return isFinite(n) && n > 0 ? n : null;
  }

  function formatMoney(amount, fmt) {
    if (amount == null || isNaN(Number(amount))) return "";
    var num = Number(amount);
    var format = fmt || "{{amount}}";
    var tokenValues = {
      amount: formatNumber(num, 2, ",", "."),
      amount_no_decimals: formatNumber(num, 0, ",", "."),
      amount_with_comma_separator: formatNumber(num, 2, ".", ","),
      amount_no_decimals_with_comma_separator: formatNumber(num, 0, ".", ","),
      amount_with_space_separator: formatNumber(num, 2, " ", ","),
      amount_no_decimals_with_space_separator: formatNumber(num, 0, " ", ","),
      amount_with_apostrophe_separator: formatNumber(num, 2, "'", "."),
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
      return Object.prototype.hasOwnProperty.call(tokenValues, token)
        ? tokenValues[token]
        : "";
    });
  }

  function normalizeVariantIdForUrl(id) {
    if (id == null || id === "") return "";
    var s = String(id).trim();
    if (s.indexOf("gid://") === 0) {
      var match = s.match(/ProductVariant\/(\d+)/i);
      return match ? match[1] : s;
    }
    return s;
  }

  /**
   * Sync hidden variant id with the selected size option. Empty / buy-all / Select
   * MUST clear a previous size's id — otherwise UI can show P while cart has M.
   */
  function syncHiddenVariantIdFromOption(hiddenId, opt) {
    if (!hiddenId) return "";
    var raw = opt && opt.value != null ? String(opt.value).trim() : "";
    if (!raw || raw === "buy-all" || raw === "Select") {
      hiddenId.value = "";
      return "";
    }
    var nid = normalizeVariantIdForUrl(raw);
    hiddenId.value = nid;
    return nid;
  }

  /**
   * Variant id Add To Basket should post. A color/size picker sends the selected
   * option's id. "Select" does not fall back to the hidden Liquid default, which
   * is a different variant than the one on screen.
   */
  function liveTvVariantIdForAdd(form) {
    if (!form) return "";
    var hidden = form.querySelector('input[name="id"]');
    var gla = form.querySelector("[data-gla-select]");
    var mla = form.querySelector("[data-mla-select]");
    var variant = form.querySelector("[data-variant-select]");
    function idOnOption(opt) {
      var raw = opt && opt.value != null ? String(opt.value).trim() : "";
      if (!raw || raw === "buy-all" || raw === "Select") return "";
      return syncHiddenVariantIdFromOption(hidden, opt);
    }
    if (gla && mla) {
      if (isInvalidSelection(gla) || mla.disabled || isInvalidSelection(mla))
        return "";
      var fromMla = idOnOption(mla.options[mla.selectedIndex]);
      if (fromMla) return fromMla;
      return hidden ? normalizeVariantIdForUrl(hidden.value) : "";
    }
    if (variant) {
      if (isInvalidSelection(variant)) return "";
      if (String(variant.value || "").trim() === "buy-all") {
        return hidden ? normalizeVariantIdForUrl(hidden.value) : "";
      }
      var fromVar = idOnOption(variant.options[variant.selectedIndex]);
      if (fromVar) return fromVar;
      return hidden ? normalizeVariantIdForUrl(hidden.value) : "";
    }
    return hidden ? normalizeVariantIdForUrl(hidden.value) : "";
  }

  /** Reject sku-data responses after the shopper already changed size. */
  function isLiveTvSkuStillSelected(select, expectedSku, expectedOpt) {
    if (!select || expectedSku == null || expectedSku === "") return true;
    var cur = select.options[select.selectedIndex];
    if (expectedOpt && cur !== expectedOpt) return false;
    var curSku = cur && cur.getAttribute("data-sku");
    return !curSku || String(curSku) === String(expectedSku);
  }

  /**
   * Apply async sku-data variant id only when still on that option and the option
   * had no id yet; also cache the id on the <option> for later synchronous sync.
   */
  function fillHiddenVariantIdFromSkuData(
    hiddenId,
    opt,
    select,
    expectedSku,
    variantId,
  ) {
    if (!hiddenId || !variantId) return;
    if (!isLiveTvSkuStillSelected(select, expectedSku, opt)) return;
    var nid = normalizeVariantIdForUrl(variantId);
    if (!nid) return;
    if (opt && (!opt.value || String(opt.value).trim() === "")) {
      opt.value = nid;
    }
    hiddenId.value = nid;
  }

  /** Shopify PDP link: `/products/handle?variant=ID`. Empty variantId strips `variant`. */
  function productUrlWithVariant(baseUrl, variantId) {
    if (!baseUrl) return "";
    var s = String(baseUrl).trim();
    if (!s || s.indexOf("/search") === 0) return s;
    var hash = "";
    var hashIdx = s.indexOf("#");
    if (hashIdx >= 0) {
      hash = s.slice(hashIdx);
      s = s.slice(0, hashIdx);
    }
    var path = s;
    var query = "";
    var qIdx = s.indexOf("?");
    if (qIdx >= 0) {
      path = s.slice(0, qIdx);
      query = s.slice(qIdx + 1);
    }
    var params = [];
    if (query) {
      query.split("&").forEach(function (part) {
        if (!part) return;
        var eq = part.indexOf("=");
        var key =
          eq >= 0
            ? decodeURIComponent(part.slice(0, eq))
            : decodeURIComponent(part);
        if (key === "variant") return;
        params.push(part);
      });
    }
    var vid = variantId != null ? String(variantId).trim() : "";
    if (vid) params.push("variant=" + encodeURIComponent(vid));
    return path + (params.length ? "?" + params.join("&") : "") + hash;
  }

  function getCardProductBaseUrl(cardEl) {
    if (!cardEl) return "";
    var base = (cardEl.getAttribute("data-product-url") || "").trim();
    if (!base) {
      var withAttr = cardEl.querySelector("[data-product-url]");
      if (withAttr)
        base = (withAttr.getAttribute("data-product-url") || "").trim();
    }
    if (base) return productUrlWithVariant(base, "");
    var mediaLink = cardEl.querySelector(
      ".missed-auction-card__media-link, .currently-on-air-card__media-link",
    );
    var titleLink = cardEl.querySelector(
      ".missed-auction-card__title a, .currently-on-air-card__title a",
    );
    var href =
      (mediaLink && mediaLink.getAttribute("href")) ||
      (titleLink && titleLink.getAttribute("href")) ||
      "";
    return productUrlWithVariant(href, "");
  }

  /** Sync media/title links from hidden input[name=id]. Buy All → plain product URL. */
  function syncCardProductLinks(cardEl, form) {
    if (!cardEl) return;
    var base = getCardProductBaseUrl(cardEl);
    if (!base || base.indexOf("/search") === 0) return;
    var variantSelect = form && form.querySelector("[data-variant-select]");
    var isBuyAll = !!(variantSelect && variantSelect.value === "buy-all");
    var hiddenId = form && form.querySelector('input[name="id"]');
    var vid =
      !isBuyAll && hiddenId ? normalizeVariantIdForUrl(hiddenId.value) : "";
    var href = productUrlWithVariant(base, vid);
    var mediaLink = cardEl.querySelector(
      ".missed-auction-card__media-link, .currently-on-air-card__media-link",
    );
    var titleLink = cardEl.querySelector(
      ".missed-auction-card__title a, .currently-on-air-card__title a",
    );
    if (mediaLink) mediaLink.href = href;
    if (titleLink) titleLink.href = href;
  }

  function decodeAttr(s) {
    if (!s) return "";
    return String(s)
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">");
  }

  /** Split a comma-separated attribute (e.g. buy-all CSVs); drops empty tokens. */
  function splitBuyAllCsvAttr(raw) {
    if (raw == null || String(raw).trim() === "") return [];
    return decodeAttr(String(raw))
      .split(",")
      .map(function (t) {
        return t.trim();
      })
      .filter(function (x) {
        return x !== "";
      });
  }

  function variantOptionDisabledForSku(selectEl, sku) {
    if (!selectEl || !sku) return false;
    var s = String(sku).trim();
    for (var i = 0; i < selectEl.options.length; i++) {
      var o = selectEl.options[i];
      if (String(o.getAttribute("data-sku") || "").trim() === s)
        return !!o.disabled;
    }
    return false;
  }

  /**
   * After GET buy-all-variants: store variant IDs plus parallel cart-* CSVs that omit
   * OOS lines so /cart/add indices align with _auctionCode / _pnp / applicable SKUs.
   */
  function applyBuyAllVariantResponse(selectEl, buyAllRow, json) {
    if (
      !buyAllRow ||
      !json ||
      !json.success ||
      !json.data ||
      !json.data.variants
    )
      return;
    var rawSkus = splitBuyAllCsvAttr(
      buyAllRow.getAttribute("data-buy-all-skus"),
    );
    var variants = json.data.variants;
    if (!Array.isArray(variants) || !rawSkus.length) return;
    var acFull = splitBuyAllCsvAttr(
      buyAllRow.getAttribute("data-buy-all-auction-codes"),
    );
    var pnpFull = splitBuyAllCsvAttr(
      buyAllRow.getAttribute("data-buy-all-pnp"),
    );
    var ids = [];
    var cartSkus = [];
    var cartAc = [];
    var cartPnp = [];
    var hasStorefrontCandidate = false;
    /* Match each entry to its sku. An older server dropped unknown skus from the list, so
       pairing by position put every later line on the wrong variant and auction code. */
    var variantBySku = {};
    for (var k = 0; k < variants.length; k++) {
      var entry = variants[k];
      if (entry && entry.sku != null)
        variantBySku[String(entry.sku).trim()] = entry;
    }
    for (var j = 0; j < rawSkus.length; j++) {
      var sku = rawSkus[j];
      var v = Object.prototype.hasOwnProperty.call(variantBySku, sku)
        ? variantBySku[sku]
        : null;
      if (!v || !v.variantId) continue;
      if (v.storefrontAvailable !== false) hasStorefrontCandidate = true;
      if (v.availableForSale === false) continue;
      if (v.storefrontAvailable === false) continue;
      if (selectEl && variantOptionDisabledForSku(selectEl, sku)) continue;
      ids.push(String(v.variantId).trim());
      cartSkus.push(sku);
      if (j < acFull.length) cartAc.push(acFull[j]);
      if (j < pnpFull.length) cartPnp.push(pnpFull[j]);
    }
    buyAllRow.setAttribute("data-buy-all-variant-ids", ids.join(","));
    buyAllRow.setAttribute("data-buy-all-cart-skus", cartSkus.join(","));
    buyAllRow.setAttribute("data-buy-all-cart-auction-codes", cartAc.join(","));
    buyAllRow.setAttribute("data-buy-all-cart-pnp", cartPnp.join(","));
    buyAllRow.setAttribute(
      "data-buy-all-storefront-unavailable",
      hasStorefrontCandidate ? "false" : "true",
    );
  }

  /* ---- Buy All variant ids ----------------------------------------------------------
     buy-all-variants can fail (app-proxy 504, network drop). Every caller used to swallow
     that with .catch(function(){}), so the ids stayed empty and the Add click only logged
     "Loading product data…" — a dead button. Now: one retry, the button stays usable, and
     a click with no ids fetches again and shows the basket error if that fails too. */
  var LTV_BUY_ALL_LOAD_ERROR_TEXT =
    "Sorry, we couldn't add this item to your basket. Please wait a moment and try again.";
  var LTV_BUY_ALL_RETRY_DELAY_MS = 1000;

  function buyAllVariantIdsOfRow(row) {
    return splitBuyAllCsvAttr(
      row && row.getAttribute("data-buy-all-variant-ids"),
    );
  }

  function selectIsOnBuyAll(select) {
    var opt = select && select.options[select.selectedIndex];
    return !!opt && String(opt.value || "").trim() === "buy-all";
  }

  /** GET buy-all-variants, retried once. Rejects on network error, non-2xx or success:false. */
  function fetchLiveTvBuyAllVariants(row) {
    var skus = splitBuyAllCsvAttr(row && row.getAttribute("data-buy-all-skus"));
    if (!skus.length)
      return Promise.reject(new Error("Buy All: the card has no skus"));
    var url =
      (row.getAttribute("data-buy-all-api-url") ||
        "/apps/live-tv/buy-all-variants") +
      "?skus=" +
      encodeURIComponent(skus.join(","));
    function once() {
      return fetch(url, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      })
        .then(function (r) {
          if (!r.ok) throw new Error("buy-all-variants HTTP " + r.status);
          return r.json();
        })
        .then(function (json) {
          if (!json || !json.success)
            throw new Error("buy-all-variants returned no variants");
          return json;
        });
    }
    return once().catch(function (err) {
      console.warn("[LTV][BuyAll] buy-all-variants failed, retrying once", err);
      return new Promise(function (resolve) {
        setTimeout(resolve, LTV_BUY_ALL_RETRY_DELAY_MS);
      }).then(once);
    });
  }

  /**
   * Load ids when the shopper picks Buy All. On failure, drop the empty ids attribute so the
   * Add button is not shown as sold out; the click then fetches again.
   * @param {function(string[])} [onApplied] - gets the in-stock ids after a good response.
   */
  function loadLiveTvBuyAllVariants(form, select, row, onApplied) {
    return fetchLiveTvBuyAllVariants(row)
      .then(function (json) {
        if (!selectIsOnBuyAll(select)) return;
        applyBuyAllVariantResponse(select, row, json);
        if (onApplied) onApplied(buyAllVariantIdsOfRow(row));
      })
      .catch(function (err) {
        if (!selectIsOnBuyAll(select)) return;
        console.warn("[LTV][BuyAll] could not load Buy All variants", err);
        row.removeAttribute("data-buy-all-variant-ids");
        syncLiveTvAddButtonState(form);
      });
  }

  /**
   * Ids for an Add / Quick Buy click. Uses the loaded ids, or fetches them now.
   * Resolves null (error already shown) when there is nothing to add.
   */
  function ensureLiveTvBuyAllVariantIds(form, select, row) {
    var ids = buyAllVariantIdsOfRow(row);
    if (ids.length) return Promise.resolve(ids);
    setAddToCartButtonLoading(form, true);
    return fetchLiveTvBuyAllVariants(row).then(
      function (json) {
        setAddToCartButtonLoading(form, false);
        if (!selectIsOnBuyAll(select)) return null;
        applyBuyAllVariantResponse(select, row, json);
        ids = buyAllVariantIdsOfRow(row);
        /* Every line sold out: the button now shows Sold out. */
        if (!ids.length) syncLiveTvAddButtonState(form);
        return ids.length ? ids : null;
      },
      function (err) {
        setAddToCartButtonLoading(form, false);
        console.warn("[LTV][BuyAll] could not load Buy All variants", err);
        showLiveTvAddError(form, { description: LTV_BUY_ALL_LOAD_ERROR_TEXT });
        return null;
      },
    );
  }

  function pnpFromAuctionItem(item) {
    if (!item || typeof item !== "object") return "";
    var v = item.PnPAmount != null ? item.PnPAmount : item.pnpAmount;
    return v != null && String(v) !== "" ? String(v) : "";
  }

  function setLineItemPnp(form, value) {
    var inp = form && form.querySelector('input[name="properties[_pnp]"]');
    if (inp) inp.value = value != null ? String(value) : "";
  }

  function applyPnpFromSelectedOption(form, opt) {
    if (!form || !opt || !opt.getAttribute) return;
    var p = opt.getAttribute("data-pnp");
    if (p != null && String(p) !== "") setLineItemPnp(form, p);
  }

  /**
   * Ring resize: stamp _isRingResize/_resize* hidden inputs from the selected option.
   * Each option adds its own size variant to cart; resize options carry data-ring-resize
   * plus the new/original sizes and the master SKU (consumed by AMS checkout validation).
   */
  function applyRingResizeFromSelectedOption(form, opt) {
    if (!form || !form.querySelector("[data-ring-resize-form]")) return;
    function setRr(name, value) {
      var inp = form.querySelector('input[name="properties[' + name + ']"]');
      if (inp) inp.value = value != null ? String(value) : "";
    }
    var isResize = !!(
      opt &&
      opt.getAttribute &&
      opt.getAttribute("data-ring-resize") === "true"
    );
    if (isResize) {
      setRr("_isRingResize", "true");
      setRr(
        "_resizeProductVariantId",
        opt.getAttribute("data-resize-variant-id") || "",
      );
      setRr("_resizeNewSize", opt.getAttribute("data-resize-new-size") || "");
      setRr(
        "_resizeOriginalSize",
        opt.getAttribute("data-resize-original-size") || "",
      );
    } else {
      setRr("_isRingResize", "");
      setRr("_resizeProductVariantId", "");
      setRr("_resizeNewSize", "");
      setRr("_resizeOriginalSize", "");
    }
  }

  /** Nth line’s _auctionCode: data-buy-all-cart-auction-codes when built for filtered Buy All, else full CSV. */
  function auctionCodeForBuyAllIndex(form, formRow, skuList, lineIndex) {
    var cartCsv =
      formRow && formRow.getAttribute("data-buy-all-cart-auction-codes");
    if (cartCsv != null && String(cartCsv).trim() !== "") {
      var cartCodes = splitBuyAllCsvAttr(cartCsv);
      if (
        cartCodes.length === skuList.length &&
        cartCodes[lineIndex] != null &&
        String(cartCodes[lineIndex]) !== ""
      ) {
        return String(cartCodes[lineIndex]);
      }
    }
    var codesCsv =
      formRow && formRow.getAttribute("data-buy-all-auction-codes");
    if (codesCsv != null && String(codesCsv).trim() !== "") {
      var codes = String(codesCsv)
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(function (x) {
          return x !== "";
        });
      if (
        codes.length === skuList.length &&
        codes[lineIndex] != null &&
        String(codes[lineIndex]) !== ""
      ) {
        return String(codes[lineIndex]);
      }
    }
    var inp = form.querySelector('input[name="properties[_auctionCode]"]');
    return inp ? String(inp.value || "") : "";
  }

  /** Nth line’s _pnp: data-buy-all-cart-pnp when present (filtered Buy All), else full CSV. */
  function pnpForBuyAllIndex(form, formRow, skuList, lineIndex) {
    var cartPnp = formRow && formRow.getAttribute("data-buy-all-cart-pnp");
    if (cartPnp != null && String(cartPnp).trim() !== "") {
      var cartPnps = splitBuyAllCsvAttr(cartPnp);
      if (
        cartPnps.length === skuList.length &&
        cartPnps[lineIndex] != null &&
        String(cartPnps[lineIndex]) !== ""
      ) {
        return String(cartPnps[lineIndex]);
      }
    }
    var csv = formRow && formRow.getAttribute("data-buy-all-pnp");
    if (csv != null && String(csv).trim() !== "") {
      var pnps = String(csv)
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(function (x) {
          return x !== "";
        });
      if (
        pnps.length === skuList.length &&
        pnps[lineIndex] != null &&
        String(pnps[lineIndex]) !== ""
      ) {
        return String(pnps[lineIndex]);
      }
    }
    var inp = form.querySelector('input[name="properties[_pnp]"]');
    return inp ? String(inp.value || "") : "";
  }

  function stockCodeFromAuctionJson(row) {
    if (!row || typeof row !== "object") return "";
    var s = row.stockCode != null ? row.stockCode : row.StockCode;
    return s != null ? String(s).trim() : "";
  }

  function auctionDisplaySize(item, variantSizeBySku) {
    if (!item || typeof item !== "object") return "";
    var sku = stockCodeFromAuctionJson(item);
    var size = String(
      item.ItemSize != null
        ? item.ItemSize
        : item.itemSize != null
          ? item.itemSize
          : item.variant != null
            ? item.variant
            : item.Variant != null
              ? item.Variant
              : (variantSizeBySku && sku && variantSizeBySku[sku]) || "",
    ).trim();
    return size || sku;
  }

  function isBuyAllStockCode(sku) {
    return !!sku && String(sku).trim().toUpperCase() === "BUYALL";
  }

  /**
   * The GLA row is itself one of the sizes — AMS puts that size's stock code on the group row and
   * leaves it out of `mlauctions` (Grey = 7898145/L with only 7898144/M nested), so the second
   * dropdown drops it unless it is added back here. The row's `variant` is the group label
   * ("Grey"), never a size, so the label comes from the Shopify variant title instead.
   */
  function glaRowAsSizeRow(ga, variantSizeBySku) {
    var sku = stockCodeFromAuctionJson(ga);
    if (!sku || isBuyAllStockCode(sku)) return null;
    var size = String(
      (variantSizeBySku && variantSizeBySku[sku]) ||
        (ga.ItemSize != null
          ? ga.ItemSize
          : ga.itemSize != null
            ? ga.itemSize
            : ""),
    ).trim();
    var row = {};
    for (var k in ga) {
      if (Object.prototype.hasOwnProperty.call(ga, k)) row[k] = ga[k];
    }
    delete row.mlauctions;
    delete row.mlaAuctions;
    row.ItemSize = size;
    row.itemSize = size;
    row.variant = size;
    row.Variant = size;
    return row;
  }

  /** MLA rows for one GLA option, with the group row's own size in front of them. */
  function withGlaRowAsSize(ga, mlaRows, variantSizeBySku) {
    var rows = Array.isArray(mlaRows) ? mlaRows.slice() : [];
    var master = glaRowAsSizeRow(ga, variantSizeBySku);
    if (!master) return rows;
    var masterSku = stockCodeFromAuctionJson(master);
    for (var i = 0; i < rows.length; i++) {
      if (stockCodeFromAuctionJson(rows[i]) === masterSku) return rows;
    }
    rows.unshift(master);
    return rows;
  }

  function sortAuctionItemsByDisplaySize(items, variantSizeBySku) {
    if (!Array.isArray(items) || items.length < 2)
      return items ? items.slice() : [];
    return items.slice().sort(function (a, b) {
      return auctionDisplaySize(a, variantSizeBySku).localeCompare(
        auctionDisplaySize(b, variantSizeBySku),
        undefined,
        { sensitivity: "base", numeric: true },
      );
    });
  }

  /** Sort size/variant options A–Z; keeps placeholder first and Buy All last. */
  function sortLiveTvSelectOptions(select) {
    if (!select || !select.options || select.options.length < 2) return;
    var opts = Array.prototype.slice.call(select.options);
    /** Removing/re-adding options below can make the browser auto-select the last-inserted
     * (Buy All) option as a side effect; capture the real selection by reference and restore it. */
    var selectedOpt = select.options[select.selectedIndex] || null;
    var head = null;
    if (opts.length && String(opts[0].value || "").trim() === "") {
      head = opts.shift();
    }
    var buyAll = [];
    var rest = [];
    opts.forEach(function (opt) {
      if (
        opt.value === "buy-all" ||
        opt.getAttribute("data-buy-all") === "true"
      )
        buyAll.push(opt);
      else rest.push(opt);
    });
    rest.sort(function (a, b) {
      return String(a.textContent || "")
        .trim()
        .localeCompare(String(b.textContent || "").trim(), undefined, {
          sensitivity: "base",
          numeric: true,
        });
    });
    while (select.options.length) select.remove(0);
    if (head) select.add(head);
    rest.forEach(function (opt) {
      select.add(opt);
    });
    buyAll.forEach(function (opt) {
      select.add(opt);
    });
    if (selectedOpt) selectedOpt.selected = true;
  }

  /**
   * Bundle SKUs for _buyAllApplicableProductCodes: data-buy-all-skus when present, else flatten MLA
   * rows from the GLA+MLA JSON config (keeps theme extension Liquid under Shopify size limits).
   */
  function buyAllApplicableSkuArrayFromLiveTvForm(form) {
    if (!form || !form.querySelector) return [];
    var row = form.querySelector("[data-buy-all-skus]");
    var csv = row && row.getAttribute("data-buy-all-skus");
    if (csv != null && String(csv).trim() !== "") {
      return String(csv)
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
    }
    var glaRow = form.querySelector("[data-gla-mla-form]");
    var fid =
      glaRow && glaRow.getAttribute && glaRow.getAttribute("data-form-id");
    if (!fid) return [];
    var scriptEl = document.getElementById(fid + "-gla-mla-config");
    if (!scriptEl || !scriptEl.textContent) return [];
    var config;
    try {
      config = JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
      return [];
    }
    var out = [];
    function pushSku(code) {
      if (!code || String(code).trim().toUpperCase() === "BUYALL") return;
      out.push(String(code).trim());
    }
    var mlaTop = config.mlaAuctions || config.mlaauctions;
    if (Array.isArray(mlaTop) && mlaTop.length) {
      for (var i = 0; i < mlaTop.length; i++) {
        pushSku(stockCodeFromAuctionJson(mlaTop[i]));
      }
      return out;
    }
    var gas = config.groupauctions || config.groupAuctions;
    if (!Array.isArray(gas)) return [];
    for (var g = 0; g < gas.length; g++) {
      var ga = gas[g];
      var mla = ga && (ga.mlauctions || ga.mlaAuctions);
      if (!Array.isArray(mla)) continue;
      for (var m = 0; m < mla.length; m++) {
        pushSku(stockCodeFromAuctionJson(mla[m]));
      }
    }
    return out;
  }

  function resolveBuyAllSkuListForForm(form, formRow, buyAllSkuList) {
    if (buyAllSkuList && buyAllSkuList.length > 0) return buyAllSkuList;
    var row = formRow || (form && form.querySelector("[data-buy-all-skus]"));
    var cartSkus = row && row.getAttribute("data-buy-all-cart-skus");
    if (cartSkus != null && String(cartSkus).trim() !== "") {
      return String(cartSkus)
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
    }
    return buyAllApplicableSkuArrayFromLiveTvForm(form);
  }

  function normalizeLiveTvItemSource(raw) {
    var source = String(raw || "")
      .trim()
      .toUpperCase();
    return source || "TV";
  }

  /**
   * Resolves once input `idInp` has a non-blank value, or after `timeoutMs`. Covers the case
   * where the variant id is only known via the async /sku-data lookup (SKU not present in
   * this card's own Liquid-config variantIdBySku map) — a cold Redis/Admin-API cache is often
   * slower than a shopper's click, and silently no-op-ing (or falling through to a native
   * submit with an empty id) produced a Shopify "Cannot find variant" error page.
   */
  function waitForLiveTvVariantId(idInp, timeoutMs) {
    return new Promise(function (resolve) {
      var existing = idInp && String(idInp.value || "").trim();
      if (existing) {
        resolve(existing);
        return;
      }
      var start = Date.now();
      var timer = setInterval(function () {
        var v = idInp && String(idInp.value || "").trim();
        if (v) {
          clearInterval(timer);
          resolve(v);
        } else if (Date.now() - start >= timeoutMs) {
          clearInterval(timer);
          resolve("");
        }
      }, 100);
    });
  }

  /* Loading state lives on the Add button (dataset flags, disabled) for both CTAs, so every existing
     guard keeps working; only the spinner moves to Quick Buy when that is what the shopper pressed. */
  function setAddToCartButtonLoading(form, loading) {
    var btn = form && form.querySelector('button[type="submit"][name="add"]');
    if (!btn) return;
    var quickBtn = form.querySelector("[data-ltv-quick-buy]");
    var spinnerBtn = form.__ltvQuickBuyPending && quickBtn ? quickBtn : btn;
    if (loading) {
      if (btn.dataset.ltvAddLoading !== "1") {
        btn.dataset.ltvPrevDisabled = btn.disabled ? "1" : "0";
      }
      btn.dataset.ltvAddLoading = "1";
      clearLiveTvAddError(form);
      spinnerBtn.classList.add("is-loading");
      spinnerBtn.setAttribute("aria-busy", "true");
      btn.disabled = true;
      if (quickBtn) quickBtn.disabled = true;
    } else {
      if (btn.dataset.ltvAddLoading !== "1") return;
      delete btn.dataset.ltvAddLoading;
      [btn, quickBtn].forEach(function (b) {
        if (!b) return;
        b.classList.remove("is-loading");
        b.removeAttribute("aria-busy");
      });
      var prev = btn.dataset.ltvPrevDisabled;
      delete btn.dataset.ltvPrevDisabled;
      btn.disabled = prev === "1";
      if (quickBtn) quickBtn.disabled = false;
    }
  }

  /* Add-to-basket failures were console-only: the spinner stopped and the shopper saw nothing at
     all. Surface Shopify's own wording ("The maximum quantity of this item is already in your
     cart", sold out, ...) just above the Add button. Built in JS so the theme extension's Liquid
     files stay under the 100 KB limit. */
  var LTV_ADD_ERROR_ICON =
    '<svg class="ltv-add-error__icon" viewBox="0 0 13 13" width="13" height="13" aria-hidden="true" focusable="false">' +
    '<circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B"/>' +
    '<path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"/>' +
    "</svg>";

  /** The error sits above the CTA row (COA) / the qty+CTA row (missed & ROA cards). */
  function liveTvAddErrorSlot(form, create) {
    if (!form) return null;
    var existing = form.querySelector("[data-ltv-add-error]");
    if (existing || !create) return existing;
    var anchor = form.querySelector(
      ".currently-on-air-card__cta-row, .missed-auction-card__form-row--qty-cta",
    );
    if (!anchor || !anchor.parentNode) return null;
    var el = document.createElement("div");
    el.className = "ltv-add-error";
    el.setAttribute("data-ltv-add-error", "");
    el.setAttribute("role", "alert");
    el.hidden = true;
    el.innerHTML =
      LTV_ADD_ERROR_ICON + '<span class="ltv-add-error__text"></span>';
    anchor.parentNode.insertBefore(el, anchor);
    return el;
  }

  /** @param {object} data - /cart/add.js body: `description` carries the shopper-facing reason, `message` is only "Cart Error". */
  function liveTvAddErrorText(data) {
    var d = data && typeof data === "object" ? data : null;
    var text = d && d.description ? String(d.description).trim() : "";
    if (
      !text &&
      d &&
      d.message &&
      String(d.message).trim().toLowerCase() !== "cart error"
    ) {
      text = String(d.message).trim();
    }
    return text || "Sorry, this item could not be added to your basket.";
  }

  function showLiveTvAddError(form, data) {
    var el = liveTvAddErrorSlot(form, true);
    if (!el) return;
    var span = el.querySelector(".ltv-add-error__text");
    if (span) span.textContent = liveTvAddErrorText(data);
    el.hidden = false;
    /* Picking a different size makes the old reason wrong — drop it on the next change. */
    if (!form.__ltvAddErrorBound) {
      form.__ltvAddErrorBound = true;
      form.addEventListener("change", function () {
        clearLiveTvAddError(form);
      });
    }
  }

  function clearLiveTvAddError(form) {
    var el = liveTvAddErrorSlot(form, false);
    if (el) el.hidden = true;
  }

  var LTV_SELECTION_CLEARED_TEXT =
    "Please choose again. That option is no longer available.";

  function showLiveTvSelectionCleared(form) {
    showLiveTvAddError(form, { description: LTV_SELECTION_CLEARED_TEXT });
  }

  /**
   * Line item properties for Live TV cart JSON.
   * @param {boolean} [includeBuyAllMetadata=true] — When false, omit _isBuyAll and buy-all-only fields (auction has no Buy All). When true (Buy All batch or single SKU from a buy-all-capable auction), include _buyAllApplicableProductCodes and related fields.
   * @param {string[]} [buyAllSkuList] — SKUs aligned with per-line data-buy-all-auction-codes / data-buy-all-pnp (Buy All batch). Use [] for single-line adds so _auctionCode/_pnp come from hidden inputs.
   * @param {string[]|undefined} [buyAllApplicableSkuList] — When set, used only for _buyAllApplicableProductCodes (full bundle). When omitted, codes use buyAllSkuList.
   */
  function collectLiveTvCartLineProperties(
    form,
    formRow,
    lineIndex,
    buyAllSkuList,
    includeBuyAllMetadata,
    buyAllApplicableSkuList,
  ) {
    if (includeBuyAllMetadata === undefined) includeBuyAllMetadata = true;
    if (!buyAllSkuList) buyAllSkuList = [];
    var skuListForApplicableCodes =
      buyAllApplicableSkuList !== undefined && buyAllApplicableSkuList !== null
        ? buyAllApplicableSkuList
        : buyAllSkuList;
    var props = {};
    var auctionOverride = auctionCodeForBuyAllIndex(
      form,
      formRow,
      buyAllSkuList,
      lineIndex,
    );
    var isBuyAllInp = form.querySelector('input[name="properties[_isBuyAll]"]');
    var isBuyAllFlag =
      isBuyAllInp && String(isBuyAllInp.value || "").toLowerCase() === "true";
    [
      ["properties[_itemSource]", "_itemSource"],
      ["properties[_auctionCode]", "_auctionCode"],
      ["properties[_pnp]", "_pnp"],
      ["properties[_isBuyAll]", "_isBuyAll"],
      ["properties[_discountCode]", "_discountCode"],
      ["properties[_buyAllPrice]", "_buyAllPrice"],
      ["properties[_buyAllDiscount]", "_buyAllDiscount"],
      [
        "properties[_buyAllApplicableProductCodes]",
        "_buyAllApplicableProductCodes",
      ],
      ["properties[_bp_min_installments]", "_bp_min_installments"],
      ["properties[_bp_plans]", "_bp_plans"],
      ["properties[_isRingResize]", "_isRingResize"],
      ["properties[_resizeProductVariantId]", "_resizeProductVariantId"],
      ["properties[_resizeNewSize]", "_resizeNewSize"],
      ["properties[_resizeOriginalSize]", "_resizeOriginalSize"],
    ].forEach(function (pair) {
      var formName = pair[0];
      var key = pair[1];
      if (key === "_buyAllApplicableProductCodes") {
        if (!includeBuyAllMetadata) return;
        var fromSkus = skuListForApplicableCodes
          .map(function (s) {
            return String(s || "").trim();
          })
          .filter(Boolean)
          .join(",");
        var codesInp = form.querySelector(
          'input[name="properties[_buyAllApplicableProductCodes]"]',
        );
        props[key] = fromSkus || (codesInp ? String(codesInp.value || "") : "");
        return;
      }
      if (
        !includeBuyAllMetadata &&
        (key === "_isBuyAll" ||
          key === "_discountCode" ||
          key === "_buyAllPrice" ||
          key === "_buyAllDiscount")
      ) {
        return;
      }
      if (
        (key === "_discountCode" ||
          key === "_buyAllPrice" ||
          key === "_buyAllDiscount") &&
        !isBuyAllFlag
      )
        return;
      if (
        formName === "properties[_auctionCode]" &&
        auctionOverride != null &&
        String(auctionOverride) !== ""
      ) {
        props[key] = String(auctionOverride);
        return;
      }
      if (formName === "properties[_itemSource]") {
        var sourceInp = form.querySelector('input[name="' + formName + '"]');
        var sourceVal = sourceInp ? sourceInp.value : "";
        props[key] = normalizeLiveTvItemSource(sourceVal);
        return;
      }
      if (formName === "properties[_pnp]") {
        var pnpOvr = pnpForBuyAllIndex(form, formRow, buyAllSkuList, lineIndex);
        if (pnpOvr != null && String(pnpOvr) !== "") {
          props[key] = String(pnpOvr);
          return;
        }
        // Keep _pnp off the line when no value is available so identical items merge.
        var pnpInp = form.querySelector('input[name="' + formName + '"]');
        var pnpVal = pnpInp ? String(pnpInp.value || "").trim() : "";
        if (pnpVal !== "") props[key] = pnpVal;
        return;
      }
      var inp = form.querySelector('input[name="' + formName + '"]');
      if (!inp) return;
      var val = String(inp.value || "").trim();
      // Budget Pay / ring resize: only stamp when a value was written (keeps non-resize lines
      // free of resize props so identical items merge).
      if (
        key === "_bp_min_installments" ||
        key === "_bp_plans" ||
        key === "_isRingResize" ||
        key === "_resizeProductVariantId" ||
        key === "_resizeNewSize" ||
        key === "_resizeOriginalSize"
      ) {
        if (val) props[key] = val;
      } else {
        props[key] = val;
      }
    });
    return props;
  }

  /**
   * Read hidden selling_plan from COA form (injected by budget-pay-livetv.js when toggle is on).
   * @returns {string|number|null}
   */
  function getSellingPlanForLiveTvAddToCart(form) {
    if (!form) return null;
    var input = null;
    var resolveSource = "none";
    var article = form.closest("article.currently-on-air-card");
    var inputId =
      article && article.getAttribute("data-bp-selling-plan-input-id");
    if (inputId) {
      var byId = document.getElementById(inputId);
      if (byId && form.contains(byId) && byId.value) {
        input = byId;
        resolveSource = "byId-in-form";
      } else if (byId && byId.value) {
        input = byId;
        resolveSource = "byId-outside-form";
      } else if (byId) {
        resolveSource = "byId-empty";
      } else {
        resolveSource = "byId-missing";
      }
    } else if (!article) {
      resolveSource = "no-article";
    }
    if (!input) {
      input = form.querySelector('input[name="selling_plan"]');
      if (input && input.value) resolveSource = "name-in-form";
    }
    if (!input || !input.value) {
      ltvBpDebugLog("getSellingPlanForLiveTvAddToCart: null", {
        resolveSource: resolveSource,
        diagnostics: getCoaBudgetPayDiagnostics(form),
      });
      return null;
    }
    var raw = String(input.value).trim();
    if (!raw) return null;
    var gidMatch = raw.match(/SellingPlan\/(\d+)/i);
    var planId = gidMatch ? Number(gidMatch[1]) : parseInt(raw, 10);
    if (Number.isNaN(planId)) planId = raw;
    ltvBpDebugLog("getSellingPlanForLiveTvAddToCart: ok", {
      resolveSource: resolveSource,
      planId: planId,
    });
    return planId;
  }

  /**
   * Build /cart/add.js line item with optional selling_plan when Budget Pay toggle is active.
   */
  function buildLiveTvCartAddItem(variantId, qty, properties, form) {
    var item = { id: variantId, quantity: qty, properties: properties || {} };
    var sellingPlan = getSellingPlanForLiveTvAddToCart(form);
    if (sellingPlan != null) item.selling_plan = sellingPlan;
    return item;
  }

  function mapCartLineItemToGtmAddToCartPayload(item) {
    if (!item) return null;
    var imageUrl =
      (item.featured_image && item.featured_image.url) || item.image || "";
    var price = item.presentment_price;
    if (price == null && item.price != null) {
      price = item.price / 100;
    }
    return {
      masterProductId:
        item.product_id != null ? String(item.product_id) : "",
      item_id: String(
        item.variant_id != null ? item.variant_id : item.id || "",
      ),
      item_name: item.product_title || item.title || "",
      item_brand: item.vendor || "",
      item_variant: item.variant_title || "",
      item_image_url: imageUrl,
      price: price != null ? price : 0,
      quantity: item.quantity || 1,
    };
  }

  function trackLiveTvGtmAddToCart(cartAddResponse) {
    var addToCart = window.gtmTrack && window.gtmTrack.addToCart;
    if (typeof addToCart !== "function") return;
    var items = cartAddResponse && cartAddResponse.items;
    if (!items || !items.length) {
      if (
        cartAddResponse &&
        (cartAddResponse.variant_id != null || cartAddResponse.id != null)
      ) {
        items = [cartAddResponse];
      } else {
        return;
      }
    }
    items.forEach(function (item) {
      var payload = mapCartLineItemToGtmAddToCartPayload(item);
      if (payload) addToCart(payload);
    });
  }

  function redirectAfterCartAdd(url, cart) {
    if (ltvUrlIsCart(url)) ltvNoteCartDeparture();
    var sync = window.syncCartTokenAttribute;
    var go = function () {
      window.location.href = url;
    };
    if (typeof sync === "function") {
      Promise.resolve(sync(cart)).then(go).catch(go);
    } else {
      go();
    }
  }

  /* ---- Basket limit ----------------------------------------------------------------
     The qty input's `max` only bounds one visit to a card. /cart/add.js merges a repeat add
     into the line that is already there, so add 3, go back, add 3 again and the basket holds 6.
     The limit is per variant per basket, which means it can only be enforced against /cart.js.

     Counted across every line holding that variant, not just Live TV ones: the cap describes how
     many units of the item a shopper may buy, and a unit bought from the PDP is still a unit. */

  function ltvFetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        if (r.status === 429) {
          var err = new Error('Too many requests. Please wait a moment and try again.');
          err._ltvTooManyRequests = true;
          throw err;
        }
        return r.ok ? r.json() : null;
      })
      .catch(function (e) {
        if (e && e._ltvTooManyRequests) throw e;
        return null;
      });
  }

  function ltvCartQtyForVariant(cart, variantId) {
    var id = String(variantId == null ? '' : variantId).trim();
    if (!id || !cart || !Array.isArray(cart.items)) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === id) {
        total += parseInt(cart.items[i].quantity, 10) || 0;
      }
    }
    return total;
  }

  /** The admin limit reaches the card as the qty input's `max`, so read it from there. */
  function ltvMaxQtyForForm(form) {
    var el =
      form &&
      form.querySelector('.currently-on-air-card__qty-input, .missed-auction-card__qty-input');
    var max = el && el.hasAttribute('max') ? parseInt(el.getAttribute('max'), 10) : NaN;
    return !isNaN(max) && max > 0 ? max : null;
  }

  function ltvBasketLimitMessage(max, inCart) {
    return (
      'You can have up to ' + max + ' of this item in your basket. It already has ' + inCart + '.'
    );
  }

  /**
   * Resolve to null when the add may go ahead, or to the shopper-facing reason when it must not.
   *
   * A cart read that fails also resolves to null: blocking a legitimate add on a network blip is
   * the worse failure, and the basket is re-checked by the same rule on the next add.
   *
   * @param {string[]} variantIds - every variant this add would put in the basket.
   * @param {number} qtyPerLine - units added per variant (Buy All adds the same qty of each).
   * @param {object|null} [prefetchedCart] - optional cart already fetched by the caller; avoids a duplicate GET /cart.js.
   */
  function ltvCheckBasketLimit(form, variantIds, qtyPerLine, prefetchedCart) {
    var max = ltvMaxQtyForForm(form);
    if (max == null) return Promise.resolve(null);
    var qty = parseInt(qtyPerLine, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    var ids = (variantIds || [])
      .map(function (v) { return String(v == null ? '' : v).trim(); })
      .filter(Boolean);
    if (!ids.length) return Promise.resolve(null);
    var cartPromise = (prefetchedCart && Array.isArray(prefetchedCart.items))
      ? Promise.resolve(prefetchedCart)
      : ltvFetchCart();
    return cartPromise.then(function (cart) {
      if (!cart) return null;
      /* The same variant twice in one Buy All payload lands on one line, so the payload's own
         repeats count towards the limit alongside what the basket already holds. */
      var wanted = {};
      for (var i = 0; i < ids.length; i++) {
        wanted[ids[i]] = (wanted[ids[i]] || 0) + qty;
      }
      for (var id in wanted) {
        if (!Object.prototype.hasOwnProperty.call(wanted, id)) continue;
        var inCart = ltvCartQtyForVariant(cart, id);
        if (inCart + wanted[id] > max) return ltvBasketLimitMessage(max, inCart);
      }
      return null;
    });
  }

  /** Stop the promise chain without the generic "could not be added" message on top. */
  function ltvBasketLimitStop(form, message) {
    setAddToCartButtonLoading(form, false);
    showLiveTvAddError(form, { description: message });
    var stop = new Error(message);
    stop._ltvBasketLimit = true;
    return stop;
  }

  function ensureBudgetPayBeforeLiveTvAdd(form) {
    var bp = window.BudgetPayLiveTv;
    if (!bp || typeof bp.syncBudgetPayStateForForm !== "function") {
      return Promise.resolve();
    }
    var bpDiag = getCoaBudgetPayDiagnostics(form);
    if (!bpDiag.toggleDataActive && !bpDiag.toggleInstanceActive) {
      return Promise.resolve();
    }
    return Promise.resolve(bp.syncBudgetPayStateForForm(form));
  }

  function ltvCartPageUrl() {
    var routes = typeof Theme !== "undefined" && Theme && Theme.routes;
    if (routes && routes.cart_url) return routes.cart_url;
    return ltvStorefrontRoot() + "cart";
  }

  function ltvReportAddBasketUnchanged(form, meta) {
    document.dispatchEvent(
      new CustomEvent("livetv:add-basket-unchanged", {
        bubbles: true,
        detail: meta || {},
      }),
    );
    if (form) {
      showLiveTvAddError(form, {
        description:
          "Sorry, we couldn't add this item to your basket. Please wait a moment and try again.",
      });
    }
  }

  /**
   * Successful add goes to the cart page, same as TJC. Opening the mini cart pins
   * the page, and on mobile Back only hides that drawer, so the pin stays and
   * ROA / Missed Auction cannot scroll.
   * @param {object|null} form
   * @param {number|null} beforeCount
   * @param {number} addedQty
   */
  function finishLiveTvCartAdd(data, redirectUrl, form, beforeCount, addedQty) {
    trackLiveTvGtmAddToCart(data);
    var qtyAdded = parseInt(addedQty, 10);
    if (isNaN(qtyAdded) || qtyAdded < 1) qtyAdded = 1;

    return ltvFetchCart()
      .then(function (cart) {
        if (!cart) {
          if (form) {
            setAddToCartButtonLoading(form, false);
            form.__ltvAddInFlight = false;
            showLiveTvAddError(form, null);
          }
          return;
        }
        var sync = window.syncCartTokenAttribute;
        var synced =
          typeof sync === "function"
            ? Promise.resolve(sync(cart))
            : Promise.resolve();
        return synced.then(function () {
          var afterCount = ltvCartItemCount(cart);
          if (
            beforeCount !== null &&
            afterCount !== null &&
            afterCount <= beforeCount
          ) {
            if (form) {
              setAddToCartButtonLoading(form, false);
              form.__ltvAddInFlight = false;
            }
            ltvReportAddBasketUnchanged(form, {
              beforeCount: beforeCount,
              afterCount: afterCount,
              addedQty: qtyAdded,
            });
            return;
          }
          redirectAfterCartAdd(
            redirectUrl === "/checkout" ? "/checkout" : ltvCartPageUrl(),
            cart,
          );
        });
      })
      .catch(function (e) {
        if (form) {
          setAddToCartButtonLoading(form, false);
          form.__ltvAddInFlight = false;
          if (e && e._ltvTooManyRequests) {
            showLiveTvAddError(form, { description: e.message });
          } else {
            showLiveTvAddError(form, null);
          }
        }
      });
  }

  function postBuyAllItemsToCart(
    form,
    formRow,
    ids,
    qty,
    buyAllSkuList,
    redirectToCheckout,
  ) {
    redirectToCheckout = !!redirectToCheckout;
    if (!ids || !ids.length) {
      console.log(
        "No in-stock variants to add for Buy All. Choose another option or refresh and try again.",
      );
      return;
    }
    setAddToCartButtonLoading(form, true);
    var ltvBuyAllBeforeCount = null;
    ltvFetchCart()
      .then(function (prefetchedCart) {
        ltvBuyAllBeforeCount = ltvCartItemCount(prefetchedCart);
        return ltvCheckBasketLimit(form, ids, qty, prefetchedCart);
      })
      .then(function (limitMsg) {
        if (limitMsg) throw ltvBasketLimitStop(form, limitMsg);
        return ensureBudgetPayBeforeLiveTvAdd(form).catch(function (err) {
          setAddToCartButtonLoading(form, false);
          var msg =
            err && err.message
              ? err.message
              : "Budget Pay could not be synced. Try again.";
          console.warn("[BudgetPay][LTV][forms]", msg);
          /* Show 429 in the add-error slot so the shopper knows to retry. */
          if (err && err._ltvTooManyRequests) {
            showLiveTvAddError(form, { description: msg });
          }
          err._ltvBpPreAddSync = true;
          throw err;
        });
      })
      .then(function () {
        var skuList = resolveBuyAllSkuListForForm(form, formRow, buyAllSkuList);
        /* data-buy-all-skus is the full, unfiltered bundle; ids/skuList already dropped
           sold-out lines. Fewer ids than bundle SKUs means this add is not actually
           buying every item in the lot, so it must not carry the Buy All discount flag
           (e.g. 2 of 3 items sold out, shopper bumps qty on the one that's left). */
        var fullBundleSkus = splitBuyAllCsvAttr(
          formRow && formRow.getAttribute("data-buy-all-skus"),
        );
        var isCompleteBuyAll =
          fullBundleSkus.length > 0 && ids.length >= fullBundleSkus.length;
        var items = ids.map(function (id, index) {
          /* _buyAllApplicableProductCodes must always be the full original bundle, not
             the availability-filtered skuList — the checkout discount Function trusts
             this property as the "required SKUs" set and applies its discount once every
             SKU named here is present in the cart. Handing it a filtered list (e.g. just
             the one surviving SKU) makes an incomplete bundle look complete to the Function. */
          var props = collectLiveTvCartLineProperties(
            form,
            formRow,
            index,
            skuList,
            true,
            fullBundleSkus,
          );
          if (!isCompleteBuyAll) {
            if ("_isBuyAll" in props) props._isBuyAll = "false";
            // Incomplete bundle: don't advertise the full-bundle codes on a line that
            // isn't actually part of a completed Buy All (also keeps AMS's inventory
            // reserve payload from grouping/deduping this as a Buy All line).
            delete props._buyAllApplicableProductCodes;
          }
          return buildLiveTvCartAddItem(id, qty, props, form);
        });
        var buyAllPayload = { items: items };
        ltvBpDebugLog("postBuyAllItemsToCart", {
          payload: buyAllPayload,
          bpDiag: getCoaBudgetPayDiagnostics(form),
        });
        return fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(buyAllPayload),
        });
      })
      .then(function (r) {
        return r.json().then(function (data) {
          return { r: r, data: data };
        });
      })
      .then(function (result) {
        if (result.r.status === 429) {
          setAddToCartButtonLoading(form, false);
          showLiveTvAddError(form, {
            description: 'Too many requests. Please wait a moment and try again.',
          });
          console.warn("[LTV][BuyAll] /cart/add.js 429 — rate limited by Shopify");
          return;
        }
        if (
          !result.r.ok ||
          (result.data && result.data.message && result.data.status)
        ) {
          setAddToCartButtonLoading(form, false);
          showLiveTvAddError(form, result.data);
          console.log(
            result.data && result.data.message
              ? result.data.message
              : "Failed to add to cart",
          );
          return;
        }
        var buyAllQty = parseInt(qty, 10);
        if (isNaN(buyAllQty) || buyAllQty < 1) buyAllQty = 1;
        return finishLiveTvCartAdd(
          result.data,
          redirectToCheckout ? "/checkout" : null,
          form,
          ltvBuyAllBeforeCount,
          buyAllQty * ids.length,
        );
      })
      .catch(function (e) {
        if (e && (e._ltvBpPreAddSync || e._ltvBasketLimit)) return;
        setAddToCartButtonLoading(form, false);
        if (e && e._ltvTooManyRequests) {
          showLiveTvAddError(form, {
            description: 'Too many requests. Please wait a moment and try again.',
          });
        } else {
          showLiveTvAddError(form, null);
        }
        console.log(e && e.message ? e.message : "Failed to add to cart");
      });
  }

  /** Single line: POST /cart/add.js with items[{ id, quantity, properties }]. Buy-all-capable auctions keep buy-all metadata so payloads match equivalent lines and avoid unintended cart splits. */
  function postSingleLiveTvItemToCart(
    form,
    formRow,
    variantId,
    qty,
    redirectToCheckout,
  ) {
    redirectToCheckout = !!redirectToCheckout;

    /* Guard: only one in-flight add per form. A double-click or mis-tap would otherwise
       fire the whole chain twice, doubling every cart API call. Cleared on success
       (page redirect) and on error so the shopper can retry. */
    if (form.__ltvAddInFlight) return;
    form.__ltvAddInFlight = true;

    setAddToCartButtonLoading(form, true);

    var ltvAddBeforeCount = null;

    /* Fetch cart ONCE and share it for both the qty-limit check and Budget Pay validation.
       Previously each step called GET /cart.js independently within milliseconds of each
       other, wasting a request on every click. */
    ltvFetchCart()
      .then(function (prefetchedCart) {
        ltvAddBeforeCount = ltvCartItemCount(prefetchedCart);
        return ltvCheckBasketLimit(form, [variantId], qty, prefetchedCart)
          .then(function (limitMsg) {
            if (limitMsg) throw ltvBasketLimitStop(form, limitMsg);
            return ensureBudgetPayBeforeLiveTvAdd(form).catch(function (err) {
              setAddToCartButtonLoading(form, false);
              form.__ltvAddInFlight = false;
              var msg =
                err && err.message
                  ? err.message
                  : "Budget Pay could not be synced. Try again.";
              console.warn("[BudgetPay][LTV][forms]", msg);
              /* Show 429 in the add-error slot so the shopper knows to retry. */
              if (err && err._ltvTooManyRequests) {
                showLiveTvAddError(form, { description: msg });
              }
              err._ltvBpPreAddSync = true;
              throw err;
            });
          });
      })
      .then(function () {
        var buyAllSkusForCodes = buyAllApplicableSkuArrayFromLiveTvForm(form);
        var buyAllRow =
          formRow ||
          form.querySelector("[data-buy-all-skus]") ||
          form.querySelector("[data-gla-mla-form]");
        var isBuyAllInpSingle = form.querySelector(
          'input[name="properties[_isBuyAll]"]',
        );
        var auctionHasBuyAll =
          isBuyAllInpSingle &&
          String(isBuyAllInpSingle.value || "").toLowerCase() === "true";
        var props = collectLiveTvCartLineProperties(
          form,
          buyAllRow || formRow || null,
          0,
          [],
          auctionHasBuyAll,
          buyAllSkusForCodes,
        );
        /* Keep _buyAllApplicableProductCodes on single-item adds too — see the matching
           comment in postBuyAllItemsToCart. Completeness is verified downstream (discount
           Function, AMS inventory reserve) against actual cart contents, not this flag. */
        if ("_isBuyAll" in props) props._isBuyAll = "false";
        var bpDiag = getCoaBudgetPayDiagnostics(form);
        var item = buildLiveTvCartAddItem(variantId, qty, props, form);
        var payload = { items: [item] };
        if (
          !item.selling_plan &&
          (bpDiag.toggleDataActive || bpDiag.toggleInstanceActive)
        ) {
          console.warn(
            "[BudgetPay][LTV][forms] Add without selling_plan while BP toggle is active",
            {
              variantId: variantId,
              bpDiag: bpDiag,
              payload: payload,
            },
          );
        }
        ltvBpDebugLog("postSingleLiveTvItemToCart", {
          payload: payload,
          bpDiag: bpDiag,
        });
        return fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
      })
      .then(function (r) {
        return r.json().then(function (data) {
          return { r: r, data: data };
        });
      })
      .then(function (result) {
        if (result.r.status === 429) {
          setAddToCartButtonLoading(form, false);
          form.__ltvAddInFlight = false;
          showLiveTvAddError(form, {
            description: 'Too many requests. Please wait a moment and try again.',
          });
          console.warn("[LTV] /cart/add.js 429 — rate limited by Shopify");
          return;
        }
        if (
          !result.r.ok ||
          (result.data && result.data.message && result.data.status)
        ) {
          setAddToCartButtonLoading(form, false);
          form.__ltvAddInFlight = false;
          showLiveTvAddError(form, result.data);
          console.warn(
            "[BudgetPay][LTV][forms] /cart/add.js failed",
            result.data,
          );
          console.log(
            result.data && result.data.message
              ? result.data.message
              : "Failed to add to cart",
          );
          return;
        }
        ltvBpDebugLog("postSingleLiveTvItemToCart: response", {
          variant_id:
            result.data &&
            (result.data.variant_id != null
              ? result.data.variant_id
              : result.data.items &&
                result.data.items[0] &&
                result.data.items[0].variant_id),
          selling_plan_allocation:
            result.data &&
            result.data.items &&
            result.data.items[0] &&
            result.data.items[0].selling_plan_allocation,
        });
        return finishLiveTvCartAdd(
          result.data,
          redirectToCheckout ? "/checkout" : null,
          form,
          ltvAddBeforeCount,
          qty,
        );
      })
      .catch(function (e) {
        /* _ltvBpPreAddSync: BP sync already called setAddToCartButtonLoading(false) and
           cleared __ltvAddInFlight in its own handler; don't double-handle.
           _ltvBasketLimit: error was already shown; just clear the in-flight guard. */
        if (e && e._ltvBpPreAddSync) return;
        if (e && e._ltvBasketLimit) {
          form.__ltvAddInFlight = false;
          return;
        }
        setAddToCartButtonLoading(form, false);
        form.__ltvAddInFlight = false;
        if (e && e._ltvTooManyRequests) {
          showLiveTvAddError(form, {
            description: 'Too many requests. Please wait a moment and try again.',
          });
        } else {
          showLiveTvAddError(form, null);
        }
        console.log(e && e.message ? e.message : "Failed to add to cart");
      });
  }

  /** COA Buy All: one POST /cart/add.js with items[] and per-line _auctionCode. */
  function runCoaBuyAll(form, redirectToCheckout) {
    redirectToCheckout = !!redirectToCheckout;
    var formRow = form.querySelector("[data-buy-all-skus]");
    if (!formRow) return;
    var select = form.querySelector("[data-variant-select]");
    if (!select || select.value !== "buy-all") return;
    var buyAllVariantIds = formRow.getAttribute("data-buy-all-variant-ids");
    var ids = (buyAllVariantIds || "")
      .split(",")
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
    if (ids.length === 0) {
      ensureLiveTvBuyAllVariantIds(form, select, formRow).then(function (got) {
        if (got) runCoaBuyAll(form, redirectToCheckout);
      });
      return;
    }
    var qtyInput = form.querySelector(".currently-on-air-card__qty-input");
    var qty = (qtyInput && parseInt(qtyInput.value, 10)) || 1;
    var buyAllSkuList = splitBuyAllCsvAttr(
      formRow.getAttribute("data-buy-all-cart-skus"),
    );
    if (!buyAllSkuList.length) {
      buyAllSkuList = (formRow.getAttribute("data-buy-all-skus") || "")
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
    }
    postBuyAllItemsToCart(
      form,
      formRow,
      ids,
      qty,
      buyAllSkuList,
      redirectToCheckout,
    );
  }

  function interceptCoaBuyAll(ev) {
    var t = ev.target;
    var form =
      t &&
      t.nodeName === "FORM" &&
      t.classList.contains("currently-on-air-card__form")
        ? t
        : null;
    if (!form && t && t.closest) {
      var btn = t.closest('button[type="submit"][name="add"]');
      if (btn && !btn.disabled) form = btn.closest("form");
    }
    if (!form || !form.classList.contains("currently-on-air-card__form"))
      return;
    var sel = form.querySelector("[data-variant-select]");
    if (
      !sel ||
      sel.value !== "buy-all" ||
      !form.querySelector("[data-buy-all-skus]")
    )
      return;
    ev.preventDefault();
    ev.stopImmediatePropagation();
    ev.stopPropagation();
    form.__ltvQuickBuyPending = false;
    runCoaBuyAll(form, false);
  }

  window.addEventListener("click", interceptCoaBuyAll, true);
  window.addEventListener("submit", interceptCoaBuyAll, true);

  /**
   * Dynamic checkout buttons (Shop Pay / wallets):
   * force the same add.js payload path as Add to Basket so line item properties match.
   */
  function interceptCoaPaymentButtonCheckout(ev) {
    if (ev.type !== "click") return;
    if (!clickIsInsideCoaPaymentButtons(ev)) return;
    var form = coaFormFromPaymentClick(ev);
    if (!form || !form.classList.contains("currently-on-air-card__form"))
      return;
    if (!form.querySelector('input[name="properties[_itemSource]"]')) return;
    if (!validateGlaMlaOrVariantForm(form)) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      ev.stopPropagation();
      return;
    }
    ev.preventDefault();
    ev.stopImmediatePropagation();
    ev.stopPropagation();
    form.__ltvQuickBuyPending = false;
    addLiveTvFormToCartThenCheckout(form);
  }

  window.addEventListener("click", interceptCoaPaymentButtonCheckout, true);

  /** Add the form's line(s) exactly as Add to Basket does, then go to /checkout instead of /cart. */
  function addLiveTvFormToCartThenCheckout(form) {
    var variantSel = form.querySelector("[data-variant-select]");
    var formRow = form.querySelector("[data-buy-all-skus]");
    var qtyEl = form.querySelector(
      ".currently-on-air-card__qty-input, .missed-auction-card__qty-input",
    );
    var qty = (qtyEl && parseInt(qtyEl.value, 10)) || 1;
    if (variantSel && variantSel.value === "buy-all" && formRow) {
      runCoaBuyAll(form, true);
      return;
    }
    var idInp = form.querySelector('input[name="id"]');
    var vid = liveTvVariantIdForAdd(form);
    if (vid) {
      postSingleLiveTvItemToCart(form, formRow || null, vid, qty, true);
      return;
    }
    setAddToCartButtonLoading(form, true);
    waitForLiveTvVariantId(idInp, 5000).then(function (resolvedVid) {
      setAddToCartButtonLoading(form, false);
      if (resolvedVid) {
        postSingleLiveTvItemToCart(
          form,
          formRow || null,
          resolvedVid,
          qty,
          true,
        );
      } else {
        console.warn(
          "[LiveTV] Checkout: variant id did not resolve in time",
          form,
        );
      }
    });
  }

  /**
   * Quick Buy (COA card + View Details popup): same validation and add as Add to Basket, then
   * /checkout. A disabled Add button (sold out, off the storefront, add in flight) blocks it too.
   */
  document.addEventListener("click", function (ev) {
    var quickBtn =
      ev.target && ev.target.closest && ev.target.closest("[data-ltv-quick-buy]");
    if (!quickBtn) return;
    var form = quickBtn.closest("form");
    if (!form || !form.querySelector('input[name="properties[_itemSource]"]'))
      return;
    ev.preventDefault();
    if (!validateGlaMlaOrVariantForm(form)) return;
    var addBtn = form.querySelector('button[type="submit"][name="add"]');
    if (addBtn && addBtn.disabled) return;
    form.__ltvQuickBuyPending = true;
    addLiveTvFormToCartThenCheckout(form);
  });

  /**
   * Single-item COA / Missed: POST /cart/add.js with items[] JSON (TV line props only; buy-all-only fields when Buy All is selected go through postBuyAllItemsToCart).
   * Skip when submitter is not the basket button (e.g. dynamic checkout / wallets).
   */
  function interceptLiveTvSingleItemCartAdd(ev) {
    if (ev.type !== "submit") return;
    var t = ev.target;
    var form = t && t.nodeName === "FORM" ? t : null;
    if (!form && t && t.closest) {
      var subBtn = t.closest('button[type="submit"][name="add"]');
      if (subBtn) form = subBtn.closest("form");
    }
    if (!form) return;
    var isCoa = form.classList.contains("currently-on-air-card__form");
    var isMa = form.classList.contains("missed-auction-card__form");
    if (!isCoa && !isMa) return;
    if (!form.querySelector('input[name="properties[_itemSource]"]')) return;
    var variantSel = form.querySelector("[data-variant-select]");
    if (
      variantSel &&
      variantSel.value === "buy-all" &&
      form.querySelector("[data-buy-all-skus]")
    )
      return;
    if (
      ev.submitter &&
      ev.submitter.getAttribute &&
      ev.submitter.getAttribute("name") !== "add"
    )
      return;
    if (!validateGlaMlaOrVariantForm(form)) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      ev.stopPropagation();
      return;
    }
    var addBtn = form.querySelector('button[type="submit"][name="add"]');
    if (addBtn && addBtn.disabled) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      ev.stopPropagation();
      return;
    }
    ev.preventDefault();
    ev.stopImmediatePropagation();
    ev.stopPropagation();
    form.__ltvQuickBuyPending = false;
    var idInp = form.querySelector('input[name="id"]');
    var vid = liveTvVariantIdForAdd(form);
    var formRow = form.querySelector("[data-buy-all-skus]");
    var qtyEl = form.querySelector(
      ".currently-on-air-card__qty-input, .missed-auction-card__qty-input",
    );
    var qty = (qtyEl && parseInt(qtyEl.value, 10)) || 1;
    if (vid) {
      postSingleLiveTvItemToCart(form, formRow || null, vid, qty, false);
      return;
    }
    setAddToCartButtonLoading(form, true);
    waitForLiveTvVariantId(idInp, 5000).then(function (resolvedVid) {
      setAddToCartButtonLoading(form, false);
      if (resolvedVid) {
        postSingleLiveTvItemToCart(
          form,
          formRow || null,
          resolvedVid,
          qty,
          false,
        );
      } else {
        console.warn(
          "[LiveTV] Add to cart: variant id did not resolve in time",
          form,
        );
      }
    });
  }

  window.addEventListener("submit", interceptLiveTvSingleItemCartAdd, true);

  /**
   * Product Bundle tab (matching-products-bundle.js): build the on-air line exactly as Add To Bag
   * would — TV flags, auction code, ring resize, Budget Pay selling plan, basket limit — so the bundle
   * can add it next to the companion items. Resolves null (errors already surfaced on the card, as for
   * Add To Bag) when the line cannot be added.
   */
  window.ltvPrepareBundleMainLine = function (card) {
    var form = card && card.querySelector("form.currently-on-air-card__form");
    if (!form || !form.querySelector('input[name="properties[_itemSource]"]')) {
      console.warn("[LTV] bundle: no Live TV form found on the card");
      return Promise.resolve(null);
    }
    if (!validateGlaMlaOrVariantForm(form)) {
      console.warn("[LTV] bundle: pick an option in the card's dropdown first");
      return Promise.resolve(null);
    }
    var vid = liveTvVariantIdForAdd(form);
    if (!vid) {
      console.warn("[LTV] bundle: the card has no variant id yet");
      return Promise.resolve(null);
    }
    return ltvFetchCart()
      .then(function (prefetchedCart) {
        return ltvCheckBasketLimit(form, [vid], 1, prefetchedCart).then(
          function (limitMsg) {
            if (limitMsg) throw ltvBasketLimitStop(form, limitMsg);
            return ensureBudgetPayBeforeLiveTvAdd(form);
          },
        );
      })
      .then(function () {
        var formRow =
          form.querySelector("[data-buy-all-skus]") ||
          form.querySelector("[data-gla-mla-form]");
        var isBuyAllInp = form.querySelector(
          'input[name="properties[_isBuyAll]"]',
        );
        var auctionHasBuyAll =
          isBuyAllInp && String(isBuyAllInp.value || "").toLowerCase() === "true";
        var props = collectLiveTvCartLineProperties(
          form,
          formRow || null,
          0,
          [],
          auctionHasBuyAll,
          buyAllApplicableSkuArrayFromLiveTvForm(form),
        );
        if ("_isBuyAll" in props) props._isBuyAll = "false";
        return buildLiveTvCartAddItem(vid, 1, props, form);
      })
      .catch(function (e) {
        if (!e || (!e._ltvBasketLimit && !e._ltvBpPreAddSync))
          showLiveTvAddError(form, null);
        console.warn("[LTV] bundle main line not added", e);
        return null;
      });
  };

  /** Standard add-to-cart: spinner on submit (may not run if single-item handler stops propagation at window). Buy-all uses postBuyAllItemsToCart. */
  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || form.nodeName !== "FORM") return;
      if (
        !form.classList.contains("missed-auction-card__form") &&
        !form.classList.contains("currently-on-air-card__form")
      )
        return;
      var variantSel = form.querySelector("[data-variant-select]");
      if (
        variantSel &&
        variantSel.value === "buy-all" &&
        form.querySelector("[data-buy-all-skus]")
      )
        return;
      var btn = form.querySelector('button[type="submit"][name="add"]');
      if (!btn || btn.disabled) return;
      setAddToCartButtonLoading(form, true);
    },
    true,
  );

  document.addEventListener(
    "change",
    function (e) {
      if (
        e.target.matches(
          "select[data-gla-select], select[data-variant-select], select[data-mla-select]",
        )
      ) {
        e.target.classList.remove("invalid-selection");
      }
    },
    true,
  );

  /* Bubble, not capture: the color handler clears the size list before this runs,
     so a new color is saved without the previous size. */
  document.addEventListener(
    "change",
    function (e) {
      if (
        !e.target.matches ||
        !e.target.matches(
          "select[data-gla-select], select[data-variant-select], select[data-mla-select]",
        )
      )
        return;
      var rememberedForm =
        e.target.closest &&
        e.target.closest(
          "form.currently-on-air-card__form, form.missed-auction-card__form",
        );
      if (rememberedForm) ltvRememberCoaSelection(rememberedForm);
    },
    false,
  );

  document.addEventListener(
    "click",
    function (e) {
      var addButton =
        e.target.closest &&
        e.target.closest(
          ".currently-on-air-card__cta--add, .missed-auction-card__cta--add",
        );
      if (!addButton) return;
      var form = addButton.closest("form");
      if (!form) return;
      if (!validateGlaMlaOrVariantForm(form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    },
    true,
  );

  /** Shop Pay / Apple Pay / Google Pay handled by interceptCoaPaymentButtonCheckout. */

  // ---- Missed auction: gallery ----
  function rebuildGallery(mediaEl, mediaUrls, galleryLabel, imageLabel) {
    if (!mediaEl || !Array.isArray(mediaUrls)) return;
    var link = mediaEl.querySelector(".missed-auction-card__media-link");
    if (!link) return;
    var count = Math.min(mediaUrls.length, 5);
    mediaEl.setAttribute("data-media-count", count);
    galleryLabel = galleryLabel || "Image gallery";
    imageLabel = imageLabel || "Image";
    if (count === 0) {
      link.innerHTML = missedAuctionPlaceholderHtml();
      var oldDots = mediaEl.querySelector(".missed-auction-card__gallery-dots");
      if (oldDots) oldDots.remove();
      return;
    }
    var primaryUrl =
      typeof mediaUrls[0] === "string"
        ? mediaUrls[0]
        : mediaUrls[0] && mediaUrls[0].url;
    var urls = mediaUrls.map(function (m) {
      return typeof m === "string" ? m : m && m.url;
    });
    var linkHtml = ltvImgTag(primaryUrl, {
      sizes: LTV_CARD_IMG_SIZES,
      className: "missed-auction-card__img",
      extra: "data-gallery-primary",
    });
    if (count >= 2 && urls[1]) {
      linkHtml += ltvImgTag(urls[1], {
        sizes: LTV_CARD_IMG_SIZES,
        className: "missed-auction-card__img missed-auction-card__img-hover",
        extra: 'data-gallery-hover aria-hidden="true"',
      });
    }
    link.innerHTML = linkHtml;
    var oldDotsEl = mediaEl.querySelector(".missed-auction-card__gallery-dots");
    if (oldDotsEl) oldDotsEl.remove();
    if (count > 1) {
      var dotsHtml = "";
      for (var i = 0; i < count; i++) {
        var dotSrc = (urls[i] || "").replace(/"/g, "&quot;");
        var active = i === 0 ? " missed-auction-card__gallery-dot--active" : "";
        dotsHtml +=
          '<button type="button" class="missed-auction-card__gallery-dot' +
          active +
          '" role="tab" aria-selected="' +
          (i === 0 ? "true" : "false") +
          '" aria-label="' +
          imageLabel +
          " " +
          (i + 1) +
          '" data-gallery-dot data-index="' +
          i +
          '" data-src="' +
          dotSrc +
          '"></button>';
      }
      var dotsWrap = document.createElement("div");
      dotsWrap.className = "missed-auction-card__gallery-dots";
      dotsWrap.setAttribute("role", "tablist");
      dotsWrap.setAttribute("aria-label", galleryLabel);
      dotsWrap.innerHTML = dotsHtml;
      mediaEl.appendChild(dotsWrap);
    }
  }

  /**
   * The live updater replaces the Currently on Air card in place. That script on the
   * store may be older than this file, so the choice has to live here, outside the DOM,
   * and be written back whenever a fresh form is initialised.
   */
  var ltvCoaSelectionMemory = {};
  var LTV_SELECTION_STORE_KEY = "ltvLiveTvSelections";

  function ltvCoaProductId(form) {
    var host = form && form.closest && form.closest("[data-product-id]");
    return host ? String(host.getAttribute("data-product-id") || "").trim() : "";
  }

  function ltvSelectionKey(form) {
    if (!form) return "";
    if (form.classList.contains("currently-on-air-card__form")) {
      return "coa:" + (form.id || "current");
    }
    var card = form.closest(".missed-auction-card, article");
    var sku = card
      ? card.getAttribute("data-product-sku") ||
        card.getAttribute("data-product-id") ||
        ""
      : "";
    return "roa:" + String(sku || form.id || "").trim();
  }

  function ltvReadSelectionStore() {
    try {
      var raw = sessionStorage.getItem(LTV_SELECTION_STORE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function ltvWriteSelectionStore(store) {
    try {
      sessionStorage.setItem(LTV_SELECTION_STORE_KEY, JSON.stringify(store || {}));
    } catch (e) {}
  }

  function ltvForgetSelection(form) {
    var key = ltvSelectionKey(form);
    if (!key) return;
    delete ltvCoaSelectionMemory[key];
    var store = ltvReadSelectionStore();
    if (!store[key]) return;
    delete store[key];
    ltvWriteSelectionStore(store);
  }

  function ltvReadSelectChoice(select) {
    if (!select || !select.options || select.selectedIndex <= 0) return null;
    var opt = select.options[select.selectedIndex];
    if (!opt) return null;
    var value = opt.value != null ? String(opt.value).trim() : "";
    if (value === "Select") return null;
    return {
      value: value,
      sku: String(opt.getAttribute("data-sku") || "").trim(),
      auctionCode: String(opt.getAttribute("data-auction-code") || "").trim(),
      label: String(opt.textContent || "").trim(),
    };
  }

  function ltvMatchSelectChoice(select, choice) {
    if (!select || !choice || !select.options) return null;
    var i;
    var opt;
    if (choice.value) {
      for (i = 0; i < select.options.length; i++) {
        opt = select.options[i];
        if (String(opt.value || "").trim() === choice.value) return opt;
      }
    }
    if (choice.sku) {
      for (i = 0; i < select.options.length; i++) {
        opt = select.options[i];
        if (String(opt.getAttribute("data-sku") || "").trim() === choice.sku)
          return opt;
      }
    }
    if (choice.label) {
      for (i = 0; i < select.options.length; i++) {
        opt = select.options[i];
        if (String(opt.textContent || "").trim() === choice.label) return opt;
      }
    }
    if (choice.auctionCode) {
      for (i = 0; i < select.options.length; i++) {
        opt = select.options[i];
        if (
          String(opt.getAttribute("data-auction-code") || "").trim() ===
          choice.auctionCode
        )
          return opt;
      }
    }
    return null;
  }

  function ltvChooseSelectOption(select, opt) {
    if (!select || !opt) return;
    opt.selected = true;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function ltvRememberCoaSelection(form) {
    if (!form || form.__ltvRestoringSelection) return;
    if (
      !form.classList.contains("currently-on-air-card__form") &&
      !form.classList.contains("missed-auction-card__form")
    )
      return;
    var key = ltvSelectionKey(form);
    if (!key) return;
    var gla = ltvReadSelectChoice(form.querySelector("[data-gla-select]"));
    var mla = ltvReadSelectChoice(form.querySelector("[data-mla-select]"));
    var variant = ltvReadSelectChoice(
      form.querySelector("[data-variant-select]"),
    );
    if (!gla && !mla && !variant) {
      ltvForgetSelection(form);
      return;
    }
    var qtyEl = form.querySelector(
      ".currently-on-air-card__qty-input, .missed-auction-card__qty-input",
    );
    var qty = qtyEl ? String(qtyEl.value == null ? "" : qtyEl.value).trim() : "";
    var snap = {
      productId: ltvCoaProductId(form),
      gla: gla,
      mla: mla,
      variant: variant,
      qty: qty && qty !== "1" ? qty : "",
    };
    ltvCoaSelectionMemory[key] = snap;
    var store = ltvReadSelectionStore();
    store[key] = snap;
    ltvWriteSelectionStore(store);
  }

  function ltvStoredSelection(form) {
    var key = ltvSelectionKey(form);
    if (!key) return null;
    if (ltvCoaSelectionMemory[key]) return ltvCoaSelectionMemory[key];
    var stored = ltvReadSelectionStore()[key];
    if (stored) ltvCoaSelectionMemory[key] = stored;
    return stored || null;
  }

  function ltvApplyRememberedQty(form, qty) {
    if (!form || !qty) return;
    var input = form.querySelector(
      ".currently-on-air-card__qty-input, .missed-auction-card__qty-input",
    );
    if (!input) return;
    /* A rebuilt card starts at 1. A Back restore can still show the qty the shopper
       set afterwards, and that live value wins over the qty saved with the size. */
    var current = String(input.value == null ? "" : input.value).trim();
    if (current && current !== "1") return;
    var n = parseInt(qty, 10);
    if (isNaN(n) || n < 1) return;
    var max = input.hasAttribute("max")
      ? parseInt(input.getAttribute("max"), 10)
      : NaN;
    if (!isNaN(max) && n > max) n = max;
    input.value = String(n);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function ltvRestoreAllLiveTvSelections() {
    document
      .querySelectorAll(
        ".currently-on-air-card__form, .missed-auction-card__form",
      )
      .forEach(function (form) {
        ltvRestoreRememberedCoaSelection(form, 0);
      });
  }

  function ltvRestoreRememberedCoaSelection(form, attempt) {
    if (!form || !form.isConnected) return;
    attempt = attempt || 0;
    if (attempt === 0 && form.__ltvRestoreRunning) return;
    var snap = ltvStoredSelection(form);
    if (!snap) return;
    var productId = ltvCoaProductId(form);
    if (snap.productId && productId && snap.productId !== productId) {
      ltvForgetSelection(form);
      return;
    }
    form.__ltvRestoreRunning = true;
    form.__ltvRestoringSelection = true;

    function retry() {
      form.__ltvRestoringSelection = false;
      setTimeout(function () {
        ltvRestoreRememberedCoaSelection(form, attempt + 1);
      }, 100);
    }

    function finish(cleared) {
      form.__ltvRestoringSelection = false;
      form.__ltvRestoreRunning = false;
      if (!cleared) return;
      ltvForgetSelection(form);
      if (typeof clearLiveTvCardSelection === "function") {
        form.__ltvRestoringSelection = true;
        clearLiveTvCardSelection(form);
        form.__ltvRestoringSelection = false;
      }
    }

    if (snap.gla) {
      var glaSelect = form.querySelector("[data-gla-select]");
      var glaOpt = ltvMatchSelectChoice(glaSelect, snap.gla);
      if (!glaOpt) {
        if (attempt < 8) {
          retry();
          return;
        }
        finish(true);
        return;
      }
      if (glaOpt.disabled) {
        finish(true);
        return;
      }
      if (snap.mla) {
        var mlaSelect = form.querySelector("[data-mla-select]");
        var mlaReady = !!(
          mlaSelect &&
          !mlaSelect.disabled &&
          mlaSelect.options.length > 1
        );
        var mlaOpt = ltvMatchSelectChoice(mlaSelect, snap.mla);
        if (!mlaOpt && !mlaReady) {
          ltvChooseSelectOption(glaSelect, glaOpt);
          mlaSelect = form.querySelector("[data-mla-select]");
          mlaOpt = ltvMatchSelectChoice(mlaSelect, snap.mla);
          mlaReady = !!(
            mlaSelect &&
            !mlaSelect.disabled &&
            mlaSelect.options.length > 1
          );
        }
        if (!mlaOpt && !mlaReady && attempt < 8) {
          retry();
          return;
        }
        if (!mlaOpt || mlaOpt.disabled) {
          if (!glaOpt.selected) ltvChooseSelectOption(glaSelect, glaOpt);
          ltvApplyRememberedQty(form, snap.qty);
          finish(true);
          return;
        }
        if (!mlaOpt.selected) ltvChooseSelectOption(mlaSelect, mlaOpt);
        ltvApplyRememberedQty(form, snap.qty);
        finish(false);
        return;
      }
      if (!glaOpt.selected) ltvChooseSelectOption(glaSelect, glaOpt);
      ltvApplyRememberedQty(form, snap.qty);
      finish(false);
      return;
    }

    if (snap.variant) {
      var variantSelect = form.querySelector("[data-variant-select]");
      var variantOpt = ltvMatchSelectChoice(variantSelect, snap.variant);
      if (!variantOpt) {
        if (attempt < 8) {
          retry();
          return;
        }
        finish(true);
        return;
      }
      if (variantOpt.disabled) {
        finish(true);
        return;
      }
      if (!variantOpt.selected) ltvChooseSelectOption(variantSelect, variantOpt);
      ltvApplyRememberedQty(form, snap.qty);
      finish(false);
      return;
    }

    finish(false);
  }

  // ---- Currently On Air form init ----
  function initCoaForm(form) {
    if (!form || form.dataset.coaFormInited === "true") return;
    form.dataset.coaFormInited = "true";
    ltvStampDefaultPrice(form.closest(".currently-on-air-card, .view-details-popup"));

    var formId = form.id;
    var hiddenId = form.querySelector('input[name="id"]');
    var qtyInput = form.querySelector(".currently-on-air-card__qty-input");
    var minusBtn = form.querySelector("[data-qty-minus]");
    var plusBtn = form.querySelector("[data-qty-plus]");
    var glaSelect = form.querySelector("[data-gla-select]");
    var mlaSelect = form.querySelector("[data-mla-select]");
    var variantSelect = form.querySelector("[data-variant-select]");
    var glaMlaRow = form.querySelector("[data-gla-mla-form]");
    if (variantSelect) sortLiveTvSelectOptions(variantSelect);
    if (glaSelect) sortLiveTvSelectOptions(glaSelect);

    var coaCard = form.closest(".currently-on-air-card");
    var coaPopup = !coaCard ? form.closest(".view-details-popup") : null;
    var coaCtx = coaCard || coaPopup;
    var coaMoneyFormatEl =
      (coaCard && coaCard.closest("[data-money-format]")) ||
      document.querySelector("[data-money-format]");
    var coaMoneyFormat = coaMoneyFormatEl
      ? coaMoneyFormatEl.getAttribute("data-money-format")
      : "";

    function updateCardFromSkuData(d, skuVal, selOpt) {
      if (!coaCtx || !d) return;
      var ctx = coaCtx;
      var mediaEl = coaCard
        ? ctx.querySelector(".currently-on-air-card__media")
        : null;
      var popupMainImg = coaPopup
        ? ctx.querySelector("[data-view-details-main]")
        : null;
      var priceEl = ctx.querySelector(
        ".currently-on-air-card__price, .view-details-popup__price",
      );
      var titleEl = ctx.querySelector(
        ".currently-on-air-card__title, .view-details-popup__product-title",
      );
      var skuEl = ctx.querySelector(
        ".currently-on-air-card__sku, .view-details-popup__sku",
      );
      var productMedia =
        d.productMedia || (d.shopifyData && d.shopifyData.productMedia);
      var mediaUrls = Array.isArray(productMedia) ? productMedia : [];
      if (
        coaPopup &&
        typeof window.updateViewDetailsGalleryFromMedia === "function" &&
        mediaUrls.length > 0
      ) {
        window.updateViewDetailsGalleryFromMedia(ctx, mediaUrls, {
          productName: (d.productName || "").trim(),
        });
      } else if (
        popupMainImg &&
        popupMainImg.tagName === "IMG" &&
        mediaUrls.length > 0
      ) {
        var primaryUrlPm =
          typeof mediaUrls[0] === "string"
            ? mediaUrls[0]
            : mediaUrls[0] && mediaUrls[0].url;
        if (primaryUrlPm) {
          popupMainImg.src = primaryUrlPm;
          popupMainImg.alt = (d.productName || "").trim() || popupMainImg.alt;
        }
      } else if (mediaEl && productMedia !== undefined) {
        var link = mediaEl.querySelector(".currently-on-air-card__media-link");
        var img =
          link &&
          link.querySelector(
            ".currently-on-air-card__img:not(.currently-on-air-card__img-hover)",
          );
        var hoverImg =
          link && link.querySelector(".currently-on-air-card__img-hover");
        if (mediaUrls.length > 0) {
          var primaryUrl =
            typeof mediaUrls[0] === "string"
              ? mediaUrls[0]
              : mediaUrls[0] && mediaUrls[0].url;
          var secondUrl =
            mediaUrls.length >= 2
              ? typeof mediaUrls[1] === "string"
                ? mediaUrls[1]
                : mediaUrls[1] && mediaUrls[1].url
              : "";
          if (link && primaryUrl) {
            if (img && img.tagName === "IMG") {
              ltvSetImgSrc(img, primaryUrl, LTV_COA_IMG_WIDTH);
              img.alt = (d.productName || "").trim() || img.alt;
              if (secondUrl && hoverImg && hoverImg.tagName === "IMG")
                ltvSetImgSrc(hoverImg, secondUrl, LTV_COA_IMG_WIDTH);
              else if (secondUrl && !hoverImg) {
                hoverImg = document.createElement("img");
                ltvSetImgSrc(hoverImg, secondUrl, LTV_COA_IMG_WIDTH);
                hoverImg.alt = "";
                hoverImg.width = 150;
                hoverImg.height = 150;
                hoverImg.loading = "lazy";
                hoverImg.className =
                  "currently-on-air-card__img currently-on-air-card__img-hover";
                hoverImg.setAttribute("aria-hidden", "true");
                link.appendChild(hoverImg);
              } else if (!secondUrl && hoverImg) hoverImg.remove();
            } else {
              var html = ltvImgTag(primaryUrl, {
                width: LTV_COA_IMG_WIDTH,
                boxWidth: 150,
                boxHeight: 150,
                className: "currently-on-air-card__img",
              });
              if (secondUrl)
                html += ltvImgTag(secondUrl, {
                  width: LTV_COA_IMG_WIDTH,
                  boxWidth: 150,
                  boxHeight: 150,
                  className:
                    "currently-on-air-card__img currently-on-air-card__img-hover",
                  extra: 'aria-hidden="true"',
                });
              link.innerHTML = html;
            }
          }
        } else if (link) link.innerHTML = coaPlaceholderHtml();
      }
      if (priceEl) {
        /* Priority: the selected auction line > the main lot's live price > the Shopify catalogue
           price. The line price is the only one that describes what this SKU actually sells for;
           sku-data returns the variant's catalogue price, which the auction overrides. */
        var linePrice = liveTvLinePriceOf(selOpt);
        /* An open popup has been re-parented to <body>, so closest() no longer reaches the slot
           carrying the live auction price; fall back to the slot itself or the popup would show
           the Shopify variant price from the sku cache while the card shows the live one. */
        var liveSlot =
          priceEl.closest("[data-ltv-live-price]") ||
          (coaPopup
            ? document.querySelector(
                "[data-currently-on-air-slot][data-ltv-live-price]",
              )
            : null);
        var livePrice =
          liveSlot && liveSlot.getAttribute("data-ltv-live-price");
        if (linePrice != null)
          priceEl.textContent = formatMoney(linePrice, coaMoneyFormat);
        else if (livePrice) priceEl.textContent = livePrice;
        else if (d.productPrice != null && d.productPrice !== "")
          priceEl.textContent = formatMoney(d.productPrice, coaMoneyFormat);
      }
      if (titleEl && d.productName) {
        var titleLink = titleEl.querySelector("a");
        var name = String(d.productName).trim();
        var text = name.length > 100 ? name.substring(0, 100) + "\u2026" : name;
        if (titleLink) titleLink.textContent = text;
        else titleEl.textContent = text;
      }
      if (skuEl && skuVal !== undefined && skuVal !== "")
        skuEl.textContent = skuVal;
      var highlightsSku = ctx.querySelector("[data-live-tv-highlights-sku]");
      if (highlightsSku && skuVal !== undefined && skuVal !== "")
        highlightsSku.textContent = skuVal;
      var highlightsAbout = ctx.querySelector(
        "[data-live-tv-highlights-about]",
      );
      if (
        highlightsAbout &&
        d.productDescription != null &&
        String(d.productDescription).trim() !== ""
      ) {
        highlightsAbout.textContent = String(d.productDescription).trim();
        var aboutSection = highlightsAbout.closest(
          ".currently-on-air-card__accordion-section--about",
        );
        if (aboutSection) aboutSection.removeAttribute("hidden");
      }
      var skuProductUrl =
        (d.productUrl && String(d.productUrl).trim()) ||
        (d.shopifyData &&
          d.shopifyData.productUrl &&
          String(d.shopifyData.productUrl).trim()) ||
        "";
      if (skuProductUrl) {
        ctx.setAttribute("data-product-url", skuProductUrl);
        var pfcCoa = form.closest("product-form-component");
        if (pfcCoa) pfcCoa.setAttribute("data-product-url", skuProductUrl);
      }
      syncCardProductLinks(ctx, form);
    }

    /**
     * Show the selected line's price immediately. The sku-data round-trip is not needed for a price
     * already stamped on the option, and the Buy All option never issues that request at all — so
     * without this, picking "Buy All (£X)" leaves the previous price on screen.
     */
    function applySelectedLinePrice(opt) {
      var linePrice = liveTvLinePriceOf(opt);
      if (linePrice == null || !coaCtx) return;
      var priceEl = coaCtx.querySelector(
        ".currently-on-air-card__price, .view-details-popup__price",
      );
      if (priceEl) priceEl.textContent = formatMoney(linePrice, coaMoneyFormat);
    }

    form.addEventListener(
      "submit",
      function (ev) {
        if (!validateGlaMlaOrVariantForm(form)) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
        }
      },
      true,
    );

    /* Prefetch availability for every auction sku up front (GLA rows + all MLA children) so
       the MLA dropdown is populated with "(sold out)" already applied — rebuilds read the
       card map synchronously — instead of options getting marked after the shopper opens it. */
    var coaGlaMlaCfgEl =
      form.querySelector('script[id$="-gla-mla-config"]') ||
      (formId ? document.getElementById(formId + "-gla-mla-config") : null);
    if (coaGlaMlaCfgEl && coaGlaMlaCfgEl.textContent) {
      try {
        var coaCfgPrefetch = JSON.parse(coaGlaMlaCfgEl.textContent.trim());
        var coaPrefetchSkus = [];
        var coaAddSku = function (it) {
          var s =
            it == null
              ? ""
              : String(
                  it.stockCode != null
                    ? it.stockCode
                    : it.StockCode != null
                      ? it.StockCode
                      : "",
                ).trim();
          if (
            s &&
            s.toUpperCase() !== "BUYALL" &&
            coaPrefetchSkus.indexOf(s) === -1
          )
            coaPrefetchSkus.push(s);
        };
        var coaCfgGas =
          coaCfgPrefetch.groupauctions || coaCfgPrefetch.groupAuctions || [];
        for (var cg = 0; cg < coaCfgGas.length; cg++) {
          coaAddSku(coaCfgGas[cg]);
          var coaCfgNested =
            (coaCfgGas[cg] &&
              (coaCfgGas[cg].mlauctions || coaCfgGas[cg].mlaAuctions)) ||
            [];
          for (var cn = 0; cn < coaCfgNested.length; cn++)
            coaAddSku(coaCfgNested[cn]);
        }
        var coaCfgMlas =
          coaCfgPrefetch.mlaAuctions || coaCfgPrefetch.MLAAuctions || [];
        for (var cm = 0; cm < coaCfgMlas.length; cm++)
          coaAddSku(coaCfgMlas[cm]);
        prefetchLiveTvSkuAvailability(form, coaPrefetchSkus);
      } catch (_) {}
    }

    if (glaSelect && mlaSelect) {
      var formRow = form.querySelector("[data-gla-mla-form]");
      if (formRow && hiddenId) {
        if (window.initMissedAuctionCardForm) {
          var cardOrPopup =
            form.closest(".currently-on-air-card") ||
            form.closest(".view-details-popup");
          if (cardOrPopup)
            window.initMissedAuctionCardForm(cardOrPopup, { glaMlaOnly: true });
        } else
          try {
            var groupauctions = [];
            var mlaAuctions = [];
            var variantSkus = [];
            var variantIds = [];
            function readConfig() {
              var el =
                form.querySelector('script[id$="-gla-mla-config"]') ||
                (formId
                  ? document.getElementById(formId + "-gla-mla-config") ||
                    form.querySelector(
                      'script[id="' + formId + '-gla-mla-config"]',
                    )
                  : null);
              if (el && el.textContent) {
                try {
                  var config = JSON.parse(el.textContent.trim());
                  return {
                    groupauctions:
                      config.groupauctions || config.groupAuctions || [],
                    mlaAuctions: config.mlaAuctions || config.MLAAuctions || [],
                    variantIdBySku: config.variantIdBySku || {},
                    variantSizeBySku: config.variantSizeBySku || {},
                    apiUrl: config.apiUrl || "",
                  };
                } catch (_) {}
              }
              return null;
            }
            var configEl =
              form.querySelector('script[id$="-gla-mla-config"]') ||
              (formId
                ? document.getElementById(formId + "-gla-mla-config")
                : null);
            var variantIdBySku = {};
            var variantSizeBySku = {};
            var apiUrl = "";
            var parsed =
              configEl && configEl.textContent
                ? (function () {
                    try {
                      return JSON.parse(configEl.textContent.trim());
                    } catch (_) {
                      return null;
                    }
                  })()
                : null;
            if (parsed) {
              groupauctions =
                parsed.groupauctions || parsed.groupAuctions || [];
              mlaAuctions = parsed.mlaAuctions || parsed.MLAAuctions || [];
              variantIdBySku = parsed.variantIdBySku || {};
              variantSizeBySku = parsed.variantSizeBySku || {};
              apiUrl = parsed.apiUrl || "";
              if (!variantIdBySku || Object.keys(variantIdBySku).length === 0) {
                variantSkus = parsed.variantSkus || [];
                variantIds = parsed.variantIds || [];
              }
            }
            if (groupauctions.length === 0 && mlaAuctions.length === 0) {
              var mlaRaw = decodeAttr(
                formRow.getAttribute("data-mla-auctions"),
              );
              try {
                if (mlaRaw) {
                  var parsed = JSON.parse(mlaRaw);
                  if (Array.isArray(parsed)) mlaAuctions = parsed;
                  else if (parsed && Array.isArray(parsed.groupauctions))
                    groupauctions = parsed.groupauctions;
                  else if (parsed && Array.isArray(parsed.groupAuctions))
                    groupauctions = parsed.groupAuctions;
                }
              } catch (_) {}
            }
            if (Object.keys(variantIdBySku).length === 0) {
              var skusRaw = decodeAttr(
                formRow.getAttribute("data-variant-skus"),
              );
              var idsRaw = decodeAttr(formRow.getAttribute("data-variant-ids"));
              try {
                if (skusRaw) variantSkus = JSON.parse(skusRaw);
              } catch (_) {}
              try {
                if (idsRaw) variantIds = JSON.parse(idsRaw);
              } catch (_) {}
              for (var i = 0; i < variantSkus.length; i++) {
                var s =
                  variantSkus[i] != null ? String(variantSkus[i]).trim() : "";
                if (s) variantIdBySku[s] = variantIds[i];
              }
            }

            function getMlaAuctionCode(item) {
              var v =
                item.MLAuctionCode != null
                  ? item.MLAuctionCode
                  : item.mlaAuctionCode != null
                    ? item.mlaAuctionCode
                    : "";
              return String(v).trim();
            }
            function getAuctionCode(item) {
              var v =
                item.AuctionCode != null
                  ? item.AuctionCode
                  : item.auctionCode != null
                    ? item.auctionCode
                    : "";
              return String(v).trim();
            }
            function getStockCode(item) {
              var v =
                item.StockCode != null
                  ? item.StockCode
                  : item.stockCode != null
                    ? item.stockCode
                    : "";
              return String(v).trim();
            }
            function getItemSize(item) {
              var v =
                item.ItemSize != null
                  ? item.ItemSize
                  : item.itemSize != null
                    ? item.itemSize
                    : item.variant != null
                      ? item.variant
                      : item.Variant != null
                        ? item.Variant
                        : "";
              return String(v).trim();
            }
            function getStockRemaining(item) {
              var v =
                item.StockRemaining != null
                  ? item.StockRemaining
                  : item.stockRemaining != null
                    ? item.stockRemaining
                    : null;
              return v != null ? Number(v) : null;
            }

            function attachGlaMlaListeners() {
              glaSelect.addEventListener("change", function () {
                var sel = glaSelect.options[glaSelect.selectedIndex];
                var glaCode = (
                  glaSelect.value ||
                  (sel && sel.getAttribute("data-auction-code")) ||
                  ""
                ).trim();
                var optionText =
                  sel && sel.textContent
                    ? String(sel.textContent).trim().toLowerCase()
                    : "";
                var glaSku = (sel && sel.getAttribute("data-stock-code")) || "";
                applySelectedLinePrice(sel);
                mlaSelect.innerHTML = "";
                mlaSelect.disabled = true;
                mlaSelect.classList.add(
                  "currently-on-air-card__select--disabled",
                );
                var placeholder = document.createElement("option");
                placeholder.value = "";
                placeholder.selected = true;
                placeholder.textContent =
                  mlaSelect.getAttribute("data-placeholder") || "Select first";
                mlaSelect.appendChild(placeholder);
                if (hiddenId) hiddenId.value = "";
                syncCardProductLinks(
                  coaCtx ||
                    form.closest(".currently-on-air-card, .view-details-popup"),
                  form,
                );
                glaSelect.classList.remove("invalid-selection");
                if (!glaCode) {
                  syncLiveTvAddButtonState(form);
                  return;
                }
                var auctionCodeInp = form.querySelector(
                  'input[name="properties[_auctionCode]"]',
                );
                if (auctionCodeInp) auctionCodeInp.value = glaCode;
                if (groupauctions.length === 0) {
                  var reconfig = readConfig();
                  if (reconfig) {
                    groupauctions = reconfig.groupauctions;
                    mlaAuctions = reconfig.mlaAuctions;
                    if (
                      reconfig.variantIdBySku &&
                      Object.keys(reconfig.variantIdBySku).length > 0
                    )
                      variantIdBySku = reconfig.variantIdBySku;
                  }
                }
                var mlaList = [];
                var selectedGroup = null;
                if (groupauctions.length > 0) {
                  selectedGroup = groupauctions.find(function (ga) {
                    var code =
                      ga.auctionCode != null
                        ? ga.auctionCode
                        : ga.AuctionCode != null
                          ? ga.AuctionCode
                          : "";
                    return (
                      String(code).trim().toLowerCase() ===
                      glaCode.toLowerCase()
                    );
                  });
                  if (!selectedGroup && optionText) {
                    selectedGroup = groupauctions.find(function (ga) {
                      var v =
                        ga.ItemSize != null
                          ? ga.ItemSize
                          : ga.itemSize != null
                            ? ga.itemSize
                            : ga.variant != null
                              ? ga.variant
                              : ga.Variant != null
                                ? ga.Variant
                                : "";
                      return String(v).trim().toLowerCase() === optionText;
                    });
                  }
                  if (selectedGroup)
                    mlaList =
                      selectedGroup.mlauctions ||
                      selectedGroup.mlaAuctions ||
                      [];
                }
                if (mlaList.length === 0 && mlaAuctions.length > 0) {
                  var g = String(glaCode).trim();
                  mlaList = mlaAuctions.filter(function (item) {
                    var ac = getAuctionCode(item);
                    var prime = String(
                      item.primeAuctionCode != null
                        ? item.primeAuctionCode
                        : item.PrimeAuctionCode != null
                          ? item.PrimeAuctionCode
                          : "",
                    ).trim();
                    var mlaC = getMlaAuctionCode(item);
                    if (ac === g) return true;
                    if (mlaC === g) return true;
                    if (prime === g && mlaC === g) return true;
                    return false;
                  });
                }
                mlaList = sortAuctionItemsByDisplaySize(
                  withGlaRowAsSize(selectedGroup, mlaList, variantSizeBySku),
                  variantSizeBySku,
                );
                var coaSoldOutSuf = liveTvVariantSoldOutSuffix(form);
                var coaInvBySku = readInvBySkuFromCard(
                  form.closest(
                    ".missed-auction-card, .currently-on-air-card, article[data-product-id]",
                  ),
                );
                mlaList.forEach(function (item) {
                  var sku = getStockCode(item);
                  var vid =
                    variantIdBySku[sku] ||
                    (sku ? variantIdBySku[String(sku).trim()] : null);
                  var stockRemaining = getStockRemaining(item);
                  var opt = document.createElement("option");
                  opt.value = vid != null ? vid : "";
                  if (sku) opt.setAttribute("data-sku", sku);
                  var mlaCode = getAuctionCode(item);
                  if (mlaCode) opt.setAttribute("data-auction-code", mlaCode);
                  var rowPnp = pnpFromAuctionItem(item);
                  if (rowPnp) opt.setAttribute("data-pnp", rowPnp);
                  var rowPrice =
                    item.currentPrice != null
                      ? item.currentPrice
                      : item.CurrentPrice;
                  if (rowPrice != null && rowPrice !== "")
                    opt.setAttribute("data-line-price", rowPrice);
                  var itemSz =
                    getItemSize(item) ||
                    (variantSizeBySku && variantSizeBySku[sku]) ||
                    "";
                  var mSold =
                    (stockRemaining !== null && stockRemaining === 0) ||
                    skuSoldOut(coaInvBySku, sku);
                  opt.textContent =
                    (itemSz
                      ? itemSz + (sku ? " (" + sku + ")" : "")
                      : sku || "") + (mSold ? coaSoldOutSuf : "");
                  opt.disabled = mSold;
                  mlaSelect.appendChild(opt);
                });
                mlaSelect.disabled = false;
                mlaSelect.classList.remove(
                  "currently-on-air-card__select--disabled",
                );
                applyPnpFromSelectedOption(
                  form,
                  glaSelect.options[glaSelect.selectedIndex],
                );
                syncLiveTvAddButtonState(form);
                prefetchLiveTvOptionAvailability(form, mlaSelect);
                if (glaSku && coaCtx) {
                  fetch(
                    "/apps/live-tv/sku-data?sku=" + encodeURIComponent(glaSku),
                    {
                      credentials: "same-origin",
                      headers: { "ngrok-skip-browser-warning": "true" },
                    },
                  )
                    .then(function (r) {
                      return r.ok ? r.json() : Promise.reject();
                    })
                    .then(function (json) {
                      if (json && json.success && json.data) {
                        /* A size picked after this color (including a selection restored
                           across a card refresh) owns the image and price. Ignore a late
                           color response so it cannot paint over that size. */
                        if (
                          mlaSelect.disabled ||
                          isInvalidSelection(mlaSelect)
                        ) {
                          updateCardFromSkuData(
                            json.data,
                            glaSku,
                            glaSelect.options[glaSelect.selectedIndex],
                          );
                        }
                        mergeSkuAvailabilityIntoCard(
                          form,
                          glaSku,
                          json.data.availableForSale,
                          json.data.storefrontAvailable,
                        );
                        syncLiveTvAddButtonState(form);
                      }
                    })
                    .catch(function () {});
                }
              });
              mlaSelect.addEventListener("change", function () {
                var opt = mlaSelect.options[mlaSelect.selectedIndex];
                var sku = opt && opt.getAttribute("data-sku");
                var mlaAuctionCode =
                  opt && opt.getAttribute("data-auction-code");
                var mlaAuctionCodeInp = form.querySelector(
                  'input[name="properties[_auctionCode]"]',
                );
                if (mlaAuctionCodeInp && mlaAuctionCode)
                  mlaAuctionCodeInp.value = mlaAuctionCode;
                applySelectedLinePrice(opt);
                applyPnpFromSelectedOption(form, opt);
                /* Sync or clear hidden id immediately. Empty option values must not
                 leave the previous size's variant id (wrong size in basket). */
                var val = syncHiddenVariantIdFromOption(hiddenId, opt);
                if (sku && hiddenId) {
                  fetch(
                    "/apps/live-tv/sku-data?sku=" + encodeURIComponent(sku),
                    {
                      credentials: "same-origin",
                      headers: { "ngrok-skip-browser-warning": "true" },
                    },
                  )
                    .then(function (r) {
                      return r.ok ? r.json() : Promise.reject();
                    })
                    .then(function (json) {
                      if (json && json.success && json.data) {
                        /* Only fill when the option had no id; ignore stale
                         responses after the shopper changed size again. */
                        if (!val && json.data.variantId) {
                          fillHiddenVariantIdFromSkuData(
                            hiddenId,
                            opt,
                            mlaSelect,
                            sku,
                            json.data.variantId,
                          );
                        }
                        if (!isLiveTvSkuStillSelected(mlaSelect, sku, opt))
                          return;
                        if (coaCtx) updateCardFromSkuData(json.data, sku, opt);
                        mergeSkuAvailabilityIntoCard(
                          form,
                          sku,
                          json.data.availableForSale,
                          json.data.storefrontAvailable,
                        );
                        markLiveTvOptionSoldOut(
                          form,
                          opt,
                          json.data.availableForSale,
                        );
                        syncLiveTvAddButtonState(form);
                        if (coaCtx) syncCardProductLinks(coaCtx, form);
                      }
                    })
                    .catch(function () {});
                  if (coaCtx) syncCardProductLinks(coaCtx, form);
                  return;
                }
                if (coaCtx) syncCardProductLinks(coaCtx, form);
                syncLiveTvAddButtonState(form);
              });
            }
            if (
              groupauctions.length === 0 &&
              mlaAuctions.length === 0 &&
              apiUrl
            ) {
              fetch(apiUrl, {
                credentials: "same-origin",
                headers: {
                  Accept: "application/json",
                  "ngrok-skip-browser-warning": "true",
                },
              })
                .then(function (r) {
                  return r.json();
                })
                .then(function (res) {
                  if (res.success && res.data) {
                    groupauctions =
                      res.data.groupauctions || res.data.groupAuctions || [];
                    mlaAuctions =
                      res.data.mlaAuctions || res.data.MLAAuctions || [];
                  }
                  attachGlaMlaListeners();
                })
                .catch(function () {
                  attachGlaMlaListeners();
                });
            } else attachGlaMlaListeners();
          } catch (e) {
            console.warn("COA GLA/MLA data parse failed", e);
          }
      }
    } else {
      var select = form.querySelector("[data-variant-select]");
      var buyAllRow = form.querySelector("[data-buy-all-skus]");
      if (select && hiddenId) {
        select.addEventListener("change", function () {
          var opt = select.options[select.selectedIndex];
          var rawVal = opt && opt.value != null ? String(opt.value).trim() : "";
          var lineAc = opt && opt.getAttribute("data-auction-code");
          var acInp = form.querySelector(
            'input[name="properties[_auctionCode]"]',
          );
          if (acInp && lineAc) acInp.value = lineAc;
          applySelectedLinePrice(opt);
          applyPnpFromSelectedOption(form, opt);
          applyRingResizeFromSelectedOption(form, opt);
          if (rawVal === "buy-all" && buyAllRow) {
            if (hiddenId) hiddenId.value = "";
            syncCardProductLinks(coaCtx, form);
            var skus = (buyAllRow.getAttribute("data-buy-all-skus") || "")
              .split(",")
              .map(function (s) {
                return s.trim();
              })
              .filter(Boolean);
            if (skus.length) {
              loadLiveTvBuyAllVariants(form, select, buyAllRow, function (ids) {
                if (ids.length && hiddenId) hiddenId.value = ids[0];
                syncCardProductLinks(coaCtx, form);
              });
            }
          } else {
            /* Clears when option value is empty so a prior size cannot stick. */
            syncHiddenVariantIdFromOption(hiddenId, opt);
            syncCardProductLinks(coaCtx, form);
          }
          var hadOptionId = !!(rawVal && rawVal !== "buy-all");
          var sku = opt && opt.getAttribute("data-sku");
          if (sku && coaCtx && rawVal !== "buy-all") {
            fetch("/apps/live-tv/sku-data?sku=" + encodeURIComponent(sku), {
              credentials: "same-origin",
              headers: { "ngrok-skip-browser-warning": "true" },
            })
              .then(function (r) {
                return r.ok ? r.json() : Promise.reject();
              })
              .then(function (json) {
                if (!json || !json.success || !json.data) return;
                var d = json.data;
                /* sku-data resolves skus store-wide; when the option already carries a
                   Liquid-resolved variant id it must win — a duplicate sku on another
                   (possibly unpublished) product would otherwise hijack the cart add. */
                if (!hadOptionId && d.variantId && hiddenId) {
                  fillHiddenVariantIdFromSkuData(
                    hiddenId,
                    opt,
                    select,
                    sku,
                    d.variantId,
                  );
                }
                if (!isLiveTvSkuStillSelected(select, sku, opt)) return;
                updateCardFromSkuData(d, sku, opt);
                mergeSkuAvailabilityIntoCard(
                  form,
                  sku,
                  d.availableForSale,
                  d.storefrontAvailable,
                );
                markLiveTvOptionSoldOut(form, opt, d.availableForSale);
                syncLiveTvAddButtonState(form);
              })
              .catch(function () {});
          }
          var addBtn = coaCtx && coaCtx.querySelector('button[name="add"]');
          var rrRowChange = form.querySelector("[data-ring-resize-form]");
          var rrMasterOos =
            rrRowChange &&
            rrRowChange.getAttribute("data-ring-resize-master-available") ===
              "false";
          if (addBtn && !rrMasterOos) {
            var labelEl = addBtn.querySelector(
              ".currently-on-air-card__cta-label",
            );
            addBtn.disabled = false;
            if (labelEl) {
              labelEl.textContent =
                addBtn.getAttribute("data-add-text") || labelEl.textContent;
            }
          }
        });
      }
    }

    syncLiveTvAddButtonState(form);
    if (variantSelect) prefetchLiveTvOptionAvailability(form, variantSelect);
    if (coaCtx) syncCardProductLinks(coaCtx, form);

    function syncCoaQtyButtonBackgrounds() {
      if (!qtyInput) return;
      var minQ = qtyInput.hasAttribute("min")
        ? parseInt(qtyInput.getAttribute("min"), 10)
        : 1;
      if (isNaN(minQ)) minQ = 1;
      var maxQ = qtyInput.hasAttribute("max")
        ? parseInt(qtyInput.getAttribute("max"), 10)
        : NaN;
      var v = parseInt(qtyInput.value, 10);
      if (isNaN(v)) v = minQ;
      if (minusBtn)
        minusBtn.classList.toggle(
          "currently-on-air-card__qty-btn--at-limit",
          v <= minQ,
        );
      if (plusBtn)
        plusBtn.classList.toggle(
          "currently-on-air-card__qty-btn--at-limit",
          !isNaN(maxQ) && v >= maxQ,
        );
    }

    if (qtyInput && minusBtn) {
      minusBtn.addEventListener("click", function () {
        var minQ = qtyInput.hasAttribute("min")
          ? parseInt(qtyInput.getAttribute("min"), 10)
          : 1;
        if (isNaN(minQ)) minQ = 1;
        var n = Math.max(minQ, parseInt(qtyInput.value, 10) - 1);
        qtyInput.value = n;
        syncCoaQtyButtonBackgrounds();
      });
    }
    if (qtyInput && plusBtn) {
      plusBtn.addEventListener("click", function () {
        var max = qtyInput.hasAttribute("max")
          ? parseInt(qtyInput.getAttribute("max"), 10)
          : NaN;
        var n = parseInt(qtyInput.value, 10) + 1;
        if (!isNaN(max)) n = Math.min(max, n);
        qtyInput.value = isNaN(n) ? 1 : n;
        syncCoaQtyButtonBackgrounds();
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener("input", syncCoaQtyButtonBackgrounds);
      qtyInput.addEventListener("change", syncCoaQtyButtonBackgrounds);
    }
    syncCoaQtyButtonBackgrounds();

    var card = form && form.closest(".currently-on-air-card");
    if (card) {
      var readMoreWrapper = card.querySelector("[data-coa-read-more-wrapper]");
      var readMoreBtn = card.querySelector("[data-coa-read-more-btn]");
      var readMoreGradient = card.querySelector(
        "[data-coa-read-more-gradient]",
      );
      var accordion = card.querySelector(".currently-on-air-card__accordion");
      if (readMoreWrapper && readMoreBtn && readMoreGradient && accordion) {
        function checkReadMore() {
          var maxH =
            parseInt(
              getComputedStyle(readMoreWrapper).getPropertyValue(
                "--coa-read-more-max-height",
              ),
              10,
            ) || 60;
          if (readMoreWrapper.scrollHeight <= maxH + 4)
            readMoreGradient.style.display = "none";
        }
        if (readMoreBtn && !readMoreBtn._coaBound) {
          readMoreBtn._coaBound = true;
          readMoreBtn.addEventListener("click", function () {
            var expanded = readMoreWrapper.classList.toggle("is-expanded");
            readMoreBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
            var label = readMoreBtn.querySelector("[data-coa-read-more-label]");
            var text = readMoreBtn.getAttribute(
              expanded ? "data-read-less-text" : "data-read-more-text",
            );
            if (label && text) label.textContent = text;
          });
        }
        accordion.addEventListener("toggle", function () {
          if (accordion.open) checkReadMore();
        });
        if (accordion.open) checkReadMore();
      }
    }
    ltvRestoreRememberedCoaSelection(form, 0);
  }

  // ---- Missed auction card init ----
  function initMissedAuctionCard(card) {
    if (!card || card.dataset.missedAuctionInited === "true") return;
    if (card.dataset.missedAuctionFormInited === "true") return;
    /* app.js owns the tab cards it renders. It now defers initCardForm to the first
       interaction / idle sweep, so its data-missed-auction-form-inited stamp is not there
       yet when it calls initMissedAuctionForms(). Without this guard both scripts wire the
       same card and every listener fires twice (qty + stepping 1 -> 3). */
    if (card.getAttribute("data-ltv-form-pending") === "1") return;
    var form = card.querySelector("form");
    if (!form) return;
    card.dataset.missedAuctionInited = "true";
    ltvStampDefaultPrice(card);

    var formId = form.id;
    var hiddenId = form.querySelector('input[name="id"]');
    var qtyInput = form.querySelector(".missed-auction-card__qty-input");
    var gallery = card.querySelector("[data-missed-auction-gallery]");
    var galleryLabel = gallery
      ? gallery.getAttribute("data-gallery-label") || "Image gallery"
      : "Image gallery";
    var imageLabel = gallery
      ? gallery.getAttribute("data-image-label") || "Image"
      : "Image";

    if (gallery) {
      gallery.addEventListener("click", function (e) {
        var dot = e.target.closest("[data-gallery-dot]");
        var prevBtn = e.target.closest("[data-gallery-prev]");
        var nextBtn = e.target.closest("[data-gallery-next]");
        var link = gallery.querySelector(".missed-auction-card__media-link");
        var primaryImg = link && link.querySelector("[data-gallery-primary]");
        var hoverImg = link && link.querySelector("[data-gallery-hover]");
        var dots = gallery.querySelectorAll("[data-gallery-dot]");
        var count = dots.length;
        if (prevBtn || nextBtn) {
          if (count === 0) return;
          e.preventDefault();
          e.stopPropagation();
          var activeDot = gallery.querySelector(
            ".missed-auction-card__gallery-dot--active",
          );
          var currentIndex = activeDot
            ? parseInt(activeDot.getAttribute("data-index"), 10) || 0
            : 0;
          var targetIndex = nextBtn
            ? (currentIndex + 1) % count
            : (currentIndex - 1 + count) % count;
          dot = dots[targetIndex];
        }
        if (!dot) return;
        e.preventDefault();
        e.stopPropagation();
        var src = dot.getAttribute("data-src");
        if (!src || !primaryImg) return;
        ltvSetImgSrc(primaryImg, src);
        if (hoverImg && count > 1) {
          var i = parseInt(dot.getAttribute("data-index"), 10) || 0;
          var nextIndex = (i + 1) % count;
          var nextDot = dots[nextIndex];
          if (nextDot)
            ltvSetImgSrc(hoverImg, nextDot.getAttribute("data-src") || src);
        }
        dots.forEach(function (d) {
          d.classList.remove("missed-auction-card__gallery-dot--active");
          d.setAttribute("aria-selected", d === dot ? "true" : "false");
        });
        dot.classList.add("missed-auction-card__gallery-dot--active");
        var clickedControl = prevBtn || nextBtn || dot;
        if (clickedControl && typeof clickedControl.blur === "function")
          clickedControl.blur();
      });
    }

    var minusBtn = form.querySelector("[data-qty-minus]");
    var plusBtn = form.querySelector("[data-qty-plus]");
    var glaSelect = form.querySelector("[data-gla-select]");
    var mlaSelect = form.querySelector("[data-mla-select]");
    var variantSelect = form.querySelector("[data-variant-select]");
    var glaMlaRow = form.querySelector("[data-gla-mla-form]");
    if (variantSelect) sortLiveTvSelectOptions(variantSelect);
    if (glaSelect) sortLiveTvSelectOptions(glaSelect);

    form.addEventListener(
      "submit",
      function (ev) {
        if (!validateGlaMlaOrVariantForm(form)) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
        }
      },
      true,
    );

    if (glaSelect && mlaSelect) {
      var formRow = form.querySelector("[data-gla-mla-form]");
      if (formRow && hiddenId) {
        if (window.initMissedAuctionCardForm) {
          window.initMissedAuctionCardForm(card, { glaMlaOnly: true });
        } else
          try {
            var groupauctions = [];
            var variantIdBySku = {};
            var variantSizeBySku = {};
            function readGroupauctionsFromCard() {
              var raw = card.getAttribute("data-groupauctions");
              if (raw) {
                try {
                  var unescaped = (raw || "").replace(/&quot;/g, '"');
                  if (unescaped) return JSON.parse(unescaped);
                } catch (_) {}
              }
              return null;
            }
            var fromAttr = readGroupauctionsFromCard();
            if (fromAttr && Array.isArray(fromAttr) && fromAttr.length > 0)
              groupauctions = fromAttr;
            else {
              var configEl =
                card.querySelector(
                  '[id="' + formId.replace(/"/g, '\\"') + '-gla-mla-config"]',
                ) || document.getElementById(formId + "-gla-mla-config");
              if (configEl && configEl.textContent) {
                try {
                  var config = JSON.parse(configEl.textContent.trim());
                  groupauctions =
                    config.groupauctions || config.groupAuctions || [];
                  variantIdBySku = config.variantIdBySku || {};
                  variantSizeBySku = config.variantSizeBySku || {};
                } catch (_) {}
              }
            }
            var configEl2 =
              card.querySelector(
                '[id="' + formId.replace(/"/g, '\\"') + '-gla-mla-config"]',
              ) || document.getElementById(formId + "-gla-mla-config");
            if (
              configEl2 &&
              configEl2.textContent &&
              variantIdBySku &&
              Object.keys(variantIdBySku).length === 0
            ) {
              try {
                var config2 = JSON.parse(configEl2.textContent.trim());
                variantIdBySku = config2.variantIdBySku || {};
                variantSizeBySku = config2.variantSizeBySku || {};
              } catch (_) {}
            }
            function getStockCode(item) {
              return String(
                item != null &&
                  (item.stockCode != null || item.StockCode != null)
                  ? item.stockCode != null
                    ? item.stockCode
                    : item.StockCode
                  : "",
              ).trim();
            }
            function readInvBySku(card) {
              if (!card || !card.getAttribute) return null;
              var raw = card.getAttribute("data-shopify-inventory");
              if (!raw) return null;
              try {
                var o = JSON.parse(String(raw).replace(/&quot;/g, '"'));
                return o &&
                  o.inventoryBySku &&
                  typeof o.inventoryBySku === "object"
                  ? o.inventoryBySku
                  : null;
              } catch (_) {
                return null;
              }
            }
            function skuSoldOut(inv, sku) {
              if (!inv || !sku) return false;
              var ent = inv[sku] || inv[String(sku).trim()];
              return !!(ent && ent.availableForSale === false);
            }

            glaSelect.addEventListener("change", function () {
              glaSelect.classList.remove("invalid-selection");
              var list = readGroupauctionsFromCard();
              if (list && Array.isArray(list) && list.length > 0)
                groupauctions = list;
              var sel = glaSelect.options[glaSelect.selectedIndex];
              var glaCode = String(
                glaSelect.value ||
                  (sel && sel.getAttribute("data-auction-code")) ||
                  "",
              ).trim();
              var optionText =
                sel && sel.textContent
                  ? String(sel.textContent).trim().toLowerCase()
                  : "";
              mlaSelect.innerHTML = "";
              mlaSelect.disabled = true;
              mlaSelect.classList.add("missed-auction-card__select--disabled");
              var placeholder = document.createElement("option");
              placeholder.value = "";
              placeholder.selected = true;
              placeholder.textContent =
                mlaSelect.getAttribute("data-placeholder") || "Select first";
              mlaSelect.appendChild(placeholder);
              if (hiddenId) hiddenId.value = "";
              syncCardProductLinks(card, form);
              if (!glaCode) {
                syncLiveTvAddButtonState(form);
                return;
              }
              var acInpMissedGla = form.querySelector(
                'input[name="properties[_auctionCode]"]',
              );
              if (acInpMissedGla) acInpMissedGla.value = glaCode;
              var selectedGroup = groupauctions.find(function (ga) {
                var code = String(
                  ga != null &&
                    (ga.auctionCode != null || ga.AuctionCode != null)
                    ? ga.auctionCode != null
                      ? ga.auctionCode
                      : ga.AuctionCode
                    : "",
                ).trim();
                return code === glaCode;
              });
              if (!selectedGroup && optionText)
                selectedGroup = groupauctions.find(function (ga) {
                  var v =
                    ga != null && (ga.variant != null || ga.Variant != null)
                      ? String(ga.variant || ga.Variant)
                          .trim()
                          .toLowerCase()
                      : "";
                  return v === optionText;
                });
              if (!selectedGroup) return;
              var mlauctions =
                selectedGroup.mlauctions || selectedGroup.mlaAuctions || [];
              if (!Array.isArray(mlauctions)) mlauctions = [];
              mlauctions = sortAuctionItemsByDisplaySize(
                withGlaRowAsSize(selectedGroup, mlauctions, variantSizeBySku),
                variantSizeBySku,
              );
              var invGrid =
                card.closest && card.closest("[data-tab-items-grid]");
              var soldOutSuf =
                (invGrid &&
                  invGrid.getAttribute("data-variant-sold-out-suffix")) ||
                " (sold out)";
              var invBySku = readInvBySku(card);
              mlauctions.forEach(function (mla) {
                var sku = getStockCode(mla);
                var vid =
                  variantIdBySku[sku] != null
                    ? variantIdBySku[sku]
                    : sku
                      ? variantIdBySku[String(sku).trim()]
                      : null;
                var mlaSize = String(
                  mla.ItemSize ||
                    mla.itemSize ||
                    mla.variant ||
                    mla.Variant ||
                    (variantSizeBySku && variantSizeBySku[sku]) ||
                    "",
                ).trim();
                var label = mlaSize ? mlaSize + " (" + sku + ")" : sku;
                var mSold = skuSoldOut(invBySku, sku);
                if (mSold) label += soldOutSuf;
                var opt = document.createElement("option");
                opt.value = vid != null ? String(vid) : "";
                if (sku) opt.setAttribute("data-sku", sku);
                var mlaPnp = pnpFromAuctionItem(mla);
                if (mlaPnp) opt.setAttribute("data-pnp", mlaPnp);
                var mlaLineAc =
                  mla && (mla.auctionCode != null || mla.AuctionCode != null)
                    ? String(
                        mla.auctionCode != null
                          ? mla.auctionCode
                          : mla.AuctionCode,
                      ).trim()
                    : "";
                if (mlaLineAc) opt.setAttribute("data-auction-code", mlaLineAc);
                opt.textContent = label;
                opt.disabled = mSold;
                mlaSelect.appendChild(opt);
              });
              mlaSelect.disabled = false;
              mlaSelect.classList.remove(
                "missed-auction-card__select--disabled",
              );
              applyPnpFromSelectedOption(
                form,
                glaSelect.options[glaSelect.selectedIndex],
              );
              syncLiveTvAddButtonState(form);
              prefetchLiveTvOptionAvailability(form, mlaSelect);
            });
            mlaSelect.addEventListener("change", function () {
              mlaSelect.classList.remove("invalid-selection");
              var opt = mlaSelect.options[mlaSelect.selectedIndex];
              var mlaLineAcM = opt && opt.getAttribute("data-auction-code");
              var acInpMlaM = form.querySelector(
                'input[name="properties[_auctionCode]"]',
              );
              if (acInpMlaM && mlaLineAcM) acInpMlaM.value = mlaLineAcM;
              applyPnpFromSelectedOption(form, opt);
              /* Sync or clear — empty option must not keep prior size's id. */
              var val = syncHiddenVariantIdFromOption(hiddenId, opt);
              syncCardProductLinks(card, form);
              var sku = opt && opt.getAttribute("data-sku");
              if (sku && hiddenId) {
                fetch("/apps/live-tv/sku-data?sku=" + encodeURIComponent(sku), {
                  credentials: "same-origin",
                  headers: { "ngrok-skip-browser-warning": "true" },
                })
                  .then(function (r) {
                    return r.ok ? r.json() : Promise.reject();
                  })
                  .then(function (json) {
                    if (json && json.success && json.data) {
                      if (!val && json.data.variantId) {
                        fillHiddenVariantIdFromSkuData(
                          hiddenId,
                          opt,
                          mlaSelect,
                          sku,
                          json.data.variantId,
                        );
                      }
                      if (!isLiveTvSkuStillSelected(mlaSelect, sku, opt))
                        return;
                      var dUrl =
                        (json.data.productUrl &&
                          String(json.data.productUrl).trim()) ||
                        "";
                      if (dUrl) {
                        card.setAttribute("data-product-url", dUrl);
                        var pfcM = form.closest("product-form-component");
                        if (pfcM) pfcM.setAttribute("data-product-url", dUrl);
                      }
                      syncCardProductLinks(card, form);
                      mergeSkuAvailabilityIntoCard(
                        form,
                        sku,
                        json.data.availableForSale,
                        json.data.storefrontAvailable,
                      );
                      markLiveTvOptionSoldOut(
                        form,
                        opt,
                        json.data.availableForSale,
                      );
                      syncLiveTvAddButtonState(form);
                    }
                  })
                  .catch(function () {});
              }
              syncLiveTvAddButtonState(form);
            });
          } catch (e) {
            console.warn("Missed auction GLA/MLA init failed", e);
          }
      }
    } else {
      var select = form.querySelector("[data-variant-select]");
      var formRowBuyAll = form.querySelector("[data-buy-all-skus]");
      if (select && hiddenId) {
        var moneyFormatEl = card.closest("[data-money-format]");
        var moneyFormat = moneyFormatEl
          ? moneyFormatEl.getAttribute("data-money-format")
          : "";
        select.addEventListener("change", function () {
          select.classList.remove("invalid-selection");
          var opt = select.options[select.selectedIndex];
          var rawVal = opt && opt.value != null ? String(opt.value).trim() : "";
          var lineAcMa = opt && opt.getAttribute("data-auction-code");
          var acInpMa = form.querySelector(
            'input[name="properties[_auctionCode]"]',
          );
          if (acInpMa && lineAcMa) acInpMa.value = lineAcMa;
          applyPnpFromSelectedOption(form, opt);
          applyRingResizeFromSelectedOption(form, opt);
          if (rawVal === "buy-all" && formRowBuyAll) {
            if (hiddenId) hiddenId.value = "";
            formRowBuyAll.setAttribute("data-buy-all-variant-ids", "");
            syncCardProductLinks(card, form);
            syncLiveTvAddButtonState(form);
            var skus = (formRowBuyAll.getAttribute("data-buy-all-skus") || "")
              .split(",")
              .map(function (s) {
                return s.trim();
              })
              .filter(Boolean);
            if (skus.length) {
              loadLiveTvBuyAllVariants(
                form,
                select,
                formRowBuyAll,
                function (ids) {
                  if (ids.length && hiddenId) hiddenId.value = ids[0];
                  syncCardProductLinks(card, form);
                  syncLiveTvAddButtonState(form);
                },
              );
            }
          } else {
            syncHiddenVariantIdFromOption(hiddenId, opt);
            syncCardProductLinks(card, form);
          }
          var hadOptionId = !!(rawVal && rawVal !== "buy-all");
          var sku = opt && opt.getAttribute("data-sku");
          if (sku && card && rawVal !== "buy-all") {
            fetch("/apps/live-tv/sku-data?sku=" + encodeURIComponent(sku), {
              credentials: "same-origin",
              headers: { "ngrok-skip-browser-warning": "true" },
            })
              .then(function (r) {
                return r.ok ? r.json() : Promise.reject();
              })
              .then(function (json) {
                if (!json || !json.success || !json.data) return;
                var d = json.data;
                /* Keep the option's own variant id when present — sku-data is a store-wide
                   lookup and duplicate skus would swap in another product's variant. */
                if (!hadOptionId && d.variantId && hiddenId) {
                  fillHiddenVariantIdFromSkuData(
                    hiddenId,
                    opt,
                    select,
                    sku,
                    d.variantId,
                  );
                }
                if (!isLiveTvSkuStillSelected(select, sku, opt)) return;
                if (d.productUrl && String(d.productUrl).trim()) {
                  card.setAttribute(
                    "data-product-url",
                    String(d.productUrl).trim(),
                  );
                  var pfcMissed = form.closest("product-form-component");
                  if (pfcMissed)
                    pfcMissed.setAttribute(
                      "data-product-url",
                      String(d.productUrl).trim(),
                    );
                }
                syncCardProductLinks(card, form);
                var mediaEl = card.querySelector(".missed-auction-card__media");
                var priceEl = card.querySelector(".missed-auction-card__price");
                var titleEl = card.querySelector(".missed-auction-card__title");
                if (mediaEl && d.productMedia !== undefined) {
                  if (d.productMedia && d.productMedia.length > 0) {
                    var urls = d.productMedia
                      .map(function (m) {
                        return typeof m === "string" ? m : m && m.url;
                      })
                      .filter(Boolean);
                    rebuildGallery(mediaEl, urls, galleryLabel, imageLabel);
                  } else rebuildGallery(mediaEl, [], galleryLabel, imageLabel);
                }
                /* The option's AMS line price wins over sku-data's Shopify catalogue price. */
                var maLinePrice = liveTvLinePriceOf(opt);
                if (priceEl && maLinePrice != null)
                  priceEl.textContent = formatMoney(maLinePrice, moneyFormat);
                else if (priceEl && d.productPrice != null && d.productPrice !== "")
                  priceEl.textContent = formatMoney(
                    d.productPrice,
                    moneyFormat,
                  );
                if (titleEl && d.productName) {
                  var titleLink = titleEl.querySelector("a");
                  var name = String(d.productName).trim();
                  var text =
                    name.length > 100
                      ? name.substring(0, 100) + "\u2026"
                      : name;
                  if (titleLink) titleLink.textContent = text;
                  else titleEl.textContent = text;
                }
                mergeSkuAvailabilityIntoCard(
                  form,
                  sku,
                  d.availableForSale,
                  d.storefrontAvailable,
                );
                markLiveTvOptionSoldOut(form, opt, d.availableForSale);
                syncLiveTvAddButtonState(form);
              })
              .catch(function () {});
          }
          syncLiveTvAddButtonState(form);
        });
      }
    }

    syncLiveTvAddButtonState(form);

    var formRowBuyAll = form.querySelector("[data-buy-all-skus]");
    if (formRowBuyAll) {
      form.addEventListener(
        "submit",
        function (ev) {
          var select = form.querySelector("[data-variant-select]");
          if (select && select.value === "buy-all") {
            ev.preventDefault();
            var buyAllVariantIds = formRowBuyAll.getAttribute(
              "data-buy-all-variant-ids",
            );
            var ids = (buyAllVariantIds || "")
              .split(",")
              .map(function (s) {
                return s.trim();
              })
              .filter(Boolean);
            if (ids.length === 0) {
              ensureLiveTvBuyAllVariantIds(form, select, formRowBuyAll).then(
                function (got) {
                  if (got) postMissedBuyAll(got);
                },
              );
              return;
            }
            postMissedBuyAll(ids);
          }
        },
        true,
      );
    }

    function postMissedBuyAll(ids) {
      var qty = (qtyInput && parseInt(qtyInput.value, 10)) || 1;
      var buyAllSkuListMa = splitBuyAllCsvAttr(
        formRowBuyAll.getAttribute("data-buy-all-cart-skus"),
      );
      if (!buyAllSkuListMa.length) {
        buyAllSkuListMa = splitBuyAllCsvAttr(
          formRowBuyAll.getAttribute("data-buy-all-skus"),
        );
      }
      postBuyAllItemsToCart(
        form,
        formRowBuyAll,
        ids,
        qty,
        buyAllSkuListMa,
        false,
      );
    }

    if (qtyInput && minusBtn)
      minusBtn.addEventListener("click", function () {
        var n = Math.max(1, parseInt(qtyInput.value, 10) - 1);
        qtyInput.value = n;
      });
    if (qtyInput && plusBtn)
      plusBtn.addEventListener("click", function () {
        var max = qtyInput.hasAttribute("max")
          ? parseInt(qtyInput.getAttribute("max"), 10)
          : NaN;
        var n = parseInt(qtyInput.value, 10) + 1;
        if (!isNaN(max)) n = Math.min(max, n);
        qtyInput.value = isNaN(n) ? 1 : n;
      });
    syncCardProductLinks(card, form);
  }

  function run() {
    document
      .querySelectorAll(".currently-on-air-card__form")
      .forEach(initCoaForm);
    document
      .querySelectorAll(".missed-auction-card")
      .forEach(initMissedAuctionCard);
  }

  /* A full reload from /cart builds the cards in run(). Drop the saved size first,
     or init writes it straight back into the new dropdowns. */
  if (ltvCameFromCartPage()) ltvDiscardRememberedSelections();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  /* A card rebuild swaps the form for a new one. initCoaForm writes the remembered
     color and size back. This covers a rebuild that does not call initCoaCardForms itself. */
  if (window.MutationObserver && document.documentElement) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            node.matches &&
            node.matches("form.currently-on-air-card__form")
          ) {
            initCoaForm(node);
          } else if (node.querySelectorAll) {
            var forms = node.querySelectorAll(
              "form.currently-on-air-card__form, form.missed-auction-card__form",
            );
            for (var k = 0; k < forms.length; k++) {
              if (forms[k].classList.contains("currently-on-air-card__form")) {
                initCoaForm(forms[k]);
              } else {
                ltvRestoreRememberedCoaSelection(forms[k], 0);
              }
            }
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * bfcache restore (browser Back from /cart) replays the DOM exactly as it was frozen:
   * the add button is still .is-loading + disabled, because a successful add deliberately
   * leaves the spinner up while the browser navigates to /cart. DOMContentLoaded does not
   * fire on restore, and syncLiveTvAddButtonState bails while ltvAddLoading is set, so
   * nothing clears it — not even re-selecting a variant. Reset it on restore.
   */
  function clearStuckAddToCartLoading() {
    document
      .querySelectorAll(
        'button[type="submit"][name="add"][data-ltv-add-loading="1"]',
      )
      .forEach(function (btn) {
        var form = btn.closest("form");
        if (form) {
          /* Clear the in-flight guard so a back-navigation (bfcache restore) doesn't
             permanently block the add button. The flag was true when the page navigated
             away; without this reset the shopper can never add again after pressing Back. */
          form.__ltvAddInFlight = false;
          setAddToCartButtonLoading(form, false);
        }
      });
  }

  /**
   * Back from cart/checkout restores the page with the shopper's color and size still
   * selected. Keep that choice when the option is still in the list and not sold out.
   * Clear it, and say so, only when that option is gone or disabled.
   */
  function liveTvPickerChoice(select) {
    if (!select) return { present: false, chosen: false, usable: false };
    if (isInvalidSelection(select))
      return { present: true, chosen: false, usable: false };
    var opt = select.options[select.selectedIndex];
    if (!opt || opt.disabled)
      return { present: true, chosen: true, usable: false };
    return { present: true, chosen: true, usable: true };
  }

  function liveTvSelectionOnRestore(form) {
    var glaSelect = form.querySelector("[data-gla-select]");
    var mlaSelect = form.querySelector("[data-mla-select]");
    var variantSelect = form.querySelector("[data-variant-select]");
    if (!glaSelect && !variantSelect) return { keep: true, hadChoice: false };
    if (glaSelect) {
      var gla = liveTvPickerChoice(glaSelect);
      if (!gla.chosen) return { keep: true, hadChoice: false };
      if (!gla.usable) return { keep: false, hadChoice: true };
      if (mlaSelect && !mlaSelect.disabled) {
        var mla = liveTvPickerChoice(mlaSelect);
        if (mla.chosen && !mla.usable) return { keep: false, hadChoice: true };
      }
      return { keep: true, hadChoice: true };
    }
    var variant = liveTvPickerChoice(variantSelect);
    if (!variant.chosen) return { keep: true, hadChoice: false };
    if (!variant.usable) return { keep: false, hadChoice: true };
    return { keep: true, hadChoice: true };
  }

  function syncKeptLiveTvSelection(form) {
    var hiddenId = form.querySelector('input[name="id"]');
    var mlaSelect = form.querySelector("[data-mla-select]");
    var variantSelect = form.querySelector("[data-variant-select]");
    if (
      variantSelect &&
      String(variantSelect.value || "").trim() === "buy-all"
    )
      return;
    var leaf =
      mlaSelect && !mlaSelect.disabled && !isInvalidSelection(mlaSelect)
        ? mlaSelect
        : variantSelect;
    if (!leaf || isInvalidSelection(leaf)) return;
    var opt = leaf.options[leaf.selectedIndex];
    var raw = opt && opt.value != null ? String(opt.value).trim() : "";
    if (!raw || raw === "buy-all" || raw === "Select") return;
    if (hiddenId) hiddenId.value = normalizeVariantIdForUrl(raw);
  }

  function clearLiveTvCardSelection(form, notify) {
    var isMissed = form.classList.contains("missed-auction-card__form");
    var glaSelect = form.querySelector("[data-gla-select]");
    var mlaSelect = form.querySelector("[data-mla-select]");
    var variantSelect = form.querySelector("[data-variant-select]");
    var hiddenId = form.querySelector('input[name="id"]');
    if (glaSelect) {
      glaSelect.selectedIndex = 0;
      glaSelect.classList.remove("invalid-selection");
    }
    if (mlaSelect) {
      mlaSelect.innerHTML = "";
      mlaSelect.disabled = true;
      mlaSelect.classList.add(
        isMissed
          ? "missed-auction-card__select--disabled"
          : "currently-on-air-card__select--disabled",
      );
      mlaSelect.classList.remove("invalid-selection");
      var placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.selected = true;
      placeholder.textContent =
        mlaSelect.getAttribute("data-placeholder") || "Select first";
      mlaSelect.appendChild(placeholder);
    }
    if (variantSelect) {
      variantSelect.selectedIndex = 0;
      variantSelect.classList.remove("invalid-selection");
    }
    /* Only a card with a picker has a variant id that depends on the shopper's selection. A
       single-variant card carries its id from Liquid and has nothing to re-pick, so clearing
       it would leave Add To Bag with no variant after Back. */
    if (hiddenId && (glaSelect || variantSelect)) hiddenId.value = "";
    syncCardProductLinks(
      form.closest(
        ".missed-auction-card, .currently-on-air-card, .view-details-popup",
      ),
      form,
    );
    syncLiveTvAddButtonState(form);
    /* Coming back from the cart resets to Select on purpose. The "choose again"
       note is only for an option that disappeared while the shopper was away. */
    if (notify !== false) showLiveTvSelectionCleared(form);
  }

  function resetLiveTvCardSelections() {
    document
      .querySelectorAll(
        ".currently-on-air-card__form, .missed-auction-card__form",
      )
      .forEach(function (form) {
        var state = liveTvSelectionOnRestore(form);
        if (!state.keep) {
          clearLiveTvCardSelection(form);
          return;
        }
        if (state.hadChoice) syncKeptLiveTvSelection(form);
      });
  }


  /* ---- Header cart count on Back --------------------------------------------------
     A successful add navigates to /cart. Pressing Back restores this page from bfcache,
     which replays the DOM exactly as it was frozen, so the header bubble still shows the
     count from before the add and only a manual refresh corrects it.

     Repaint that bubble and nothing else. The theme-wide `cart:update` event is not usable
     here: it announces that the cart just changed, which on a restore is untrue — the theme
     acts on it by sending the shopper to /cart, and budget-pay-livetv.js re-syncs every
     active toggle off it. Write the count into the bubble instead, and re-render the bubble
     section only when there is no bubble to write to, which is how a cart that was empty
     when the page froze gets one. */

  /** Locale-aware storefront root, always with a trailing slash. */
  function ltvStorefrontRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root) return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function ltvCartItemCount(cart) {
    if (!cart || typeof cart !== 'object') return null;
    if (typeof cart.item_count === 'number' && !isNaN(cart.item_count)) {
      return cart.item_count;
    }
    if (!Array.isArray(cart.items)) return null;
    return cart.items.reduce(function (sum, line) {
      return sum + (parseInt(line.quantity, 10) || 0);
    }, 0);
  }

  /**
   * @returns {boolean} false when the header holds no bubble to write into — the theme omits
   *   it entirely for an empty cart, so the caller has to re-render the section instead.
   */
  function ltvWriteCartBubble(itemCount) {
    var text = String(itemCount);
    var counts = document.querySelectorAll(
      '.cart-bubble__text-count, [data-testid="cart-bubble"], [ref="cartBubbleCount"]'
    );
    if (!counts.length) return false;
    counts.forEach(function (el) {
      el.textContent = text;
    });
    document.querySelectorAll('.cart-bubble .visually-hidden').forEach(function (el) {
      var t = el.textContent || '';
      if (/total items in cart/i.test(t)) {
        el.textContent = t.replace(/\d+/, text);
      }
    });
    return true;
  }

  /** The header's own section wrapper, whatever the theme named it. */
  function ltvCartBubbleSection() {
    return (
      document.getElementById('shopify-section-cart-icon-bubble') ||
      document.querySelector('[id^="shopify-section-"][id*="cart-icon"]')
    );
  }

  /** Re-render just the header bubble section and swap its markup in. */
  function ltvSwapCartBubbleSection() {
    var host = ltvCartBubbleSection();
    if (!host) return Promise.resolve();
    var id = host.id.replace(/^shopify-section-/, '');
    return fetch(ltvStorefrontRoot() + '?sections=' + encodeURIComponent(id), {
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (sections) {
        var html = sections && sections[id];
        if (typeof html !== 'string' || !html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var rendered = doc.getElementById('shopify-section-' + id);
        host.innerHTML = rendered ? rendered.innerHTML : html;
      })
      .catch(function () {});
  }

  function refreshHeaderCartCount() {
    return ltvFetchCart().then(function (cart) {
      var count = ltvCartItemCount(cart);
      if (count === null) return;
      if (ltvWriteCartBubble(count)) return;
      if (count === 0) return;
      return ltvSwapCartBubbleSection();
    });
  }

  /** True when this load is a Back/Forward the browser answered without bfcache. */
  function ltvIsBackForwardNavigation() {
    try {
      var nav =
        window.performance &&
        typeof window.performance.getEntriesByType === 'function' &&
        window.performance.getEntriesByType('navigation')[0];
      return !!nav && nav.type === 'back_forward';
    } catch (e) {
      return false;
    }
  }

  var LTV_RETURN_FROM_CART_KEY = 'ltvReturnFromCart';

  function ltvUrlIsCart(url) {
    return /(?:^|\/)cart(?:[/?#]|$)/.test(String(url || ''));
  }

  /** Set before Add to Bag, Edit Basket, or the header bag navigates to /cart. */
  function ltvNoteCartDeparture() {
    try {
      sessionStorage.setItem(LTV_RETURN_FROM_CART_KEY, '1');
    } catch (e) {}
  }

  function ltvClearCartDeparture() {
    try {
      sessionStorage.removeItem(LTV_RETURN_FROM_CART_KEY);
    } catch (e) {}
  }

  function ltvCameFromCartPage() {
    try {
      if (sessionStorage.getItem(LTV_RETURN_FROM_CART_KEY) === '1') return true;
    } catch (e) {}
    try {
      if (ltvUrlIsCart(document.referrer || '')) return true;
    } catch (e2) {}
    try {
      var from =
        window.navigation &&
        window.navigation.activation &&
        window.navigation.activation.from;
      if (from && ltvUrlIsCart(from.url || '')) return true;
    } catch (e3) {}
    return false;
  }

  function ltvDiscardRememberedSelections() {
    ltvCoaSelectionMemory = {};
    ltvWriteSelectionStore({});
  }

  function ltvStampDefaultPrice(root) {
    if (!root || !root.querySelectorAll) return;
    var prices = root.querySelectorAll(
      '.currently-on-air-card__price, .missed-auction-card__price, .view-details-popup__price',
    );
    for (var i = 0; i < prices.length; i++) {
      if (!prices[i].hasAttribute('data-ltv-default-price')) {
        prices[i].setAttribute('data-ltv-default-price', prices[i].textContent || '');
      }
    }
  }

  /**
   * Leaving for /cart can freeze the page while it is still pinned (body position:fixed
   * from the mini cart, or a popup). Back then restores that pin, so ROA will not scroll.
   */
  function releaseIwRoaScrollLock() {
    var root = document.documentElement;
    var body = document.body;
    if (!body) return;

    var restoreY = 0;
    if (body.style.position === 'fixed' && body.style.top) {
      var parsed = parseInt(body.style.top, 10);
      if (!isNaN(parsed)) restoreY = Math.abs(parsed);
    }

    document.querySelectorAll('dialog[open]').forEach(function (dialog) {
      try {
        dialog.close();
      } catch (e) {}
    });
    document.querySelectorAll('.view-details-popup.is-open').forEach(function (popup) {
      popup.classList.remove('is-open');
      popup.setAttribute('hidden', '');
      popup.style.display = '';
    });
    document.querySelectorAll('.missed-filters__popup.is-open').forEach(function (popup) {
      popup.classList.remove('is-open');
      popup.hidden = true;
    });

    root.removeAttribute('scroll-lock');
    root.style.overflow = '';
    body.style.overflow = '';
    body.style.position = '';
    body.style.top = '';
    body.style.width = '';
    body.style.left = '';
    body.style.right = '';

    if (restoreY) window.scrollTo(0, restoreY);

    /* html { scroll-behavior: smooth } can leave Back's scroll restoration
       running, which swallows the wheel. One auto frame ends it. */
    var prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    void root.offsetWidth;
    root.style.scrollBehavior = prevBehavior;
  }

  /** Select, quantity 1, and the price from before a size was chosen. No error message. */
  function ltvResetCardsToPlaceholder() {
    document
      .querySelectorAll('.currently-on-air-card__form, .missed-auction-card__form')
      .forEach(function (form) {
        clearLiveTvCardSelection(form, false);
        var qtyInput = form.querySelector(
          '.missed-auction-card__qty-input, .currently-on-air-card__qty-input',
        );
        if (qtyInput) {
          var minQ = parseInt(qtyInput.getAttribute('min'), 10);
          qtyInput.value = String(!isNaN(minQ) && minQ > 0 ? minQ : 1);
        }
        var card = form.closest(
          '.missed-auction-card, .currently-on-air-card, .view-details-popup',
        );
        if (!card) return;
        card.querySelectorAll('[data-ltv-default-price]').forEach(function (priceEl) {
          priceEl.textContent = priceEl.getAttribute('data-ltv-default-price') || '';
        });
      });
  }

  document.addEventListener(
    'click',
    function (e) {
      var link = e.target && e.target.closest && e.target.closest('a[href]');
      if (!link || !ltvUrlIsCart(link.getAttribute('href'))) return;
      ltvNoteCartDeparture();
    },
    true,
  );

  window.addEventListener('pagehide', function () {
    releaseIwRoaScrollLock();
  });

  window.addEventListener('pageshow', function (ev) {
    var fromHistory = ev.persisted || ltvIsBackForwardNavigation();
    var fromCart = ltvCameFromCartPage();
    if (!fromHistory && !fromCart) return;

    /* Back restores a frozen ROA page. Drop the pin before anything else. */
    releaseIwRoaScrollLock();

    if (fromHistory) {
      /* A Back that missed bfcache is still answered from the HTTP cache, so the header
         count is stale there too. A first load already carries the right count. */
      refreshHeaderCartCount();
      clearStuckAddToCartLoading();
      /* Safety net: even if the button loading marker was somehow cleared before the page
         navigated away, the in-flight guard could still be true. Reset it on all Live TV
         forms so a bfcache restore never leaves the add button permanently stuck. */
      document
        .querySelectorAll(
          'form.currently-on-air-card__form, form.missed-auction-card__form',
        )
        .forEach(function (form) {
          form.__ltvAddInFlight = false;
        });
    }

    /* A product-page Back still puts the saved size back. Only the cart clears it. */
    if (!fromCart) {
      ltvRestoreAllLiveTvSelections();
      return;
    }

    ltvDiscardRememberedSelections();
    ltvResetCardsToPlaceholder();
    ltvClearCartDeparture();
    /* Several browsers write the old <select> value back after pageshow. */
    setTimeout(ltvResetCardsToPlaceholder, 0);
    setTimeout(function () {
      releaseIwRoaScrollLock();
      ltvResetCardsToPlaceholder();
    }, 50);
  });

  /* The Budget Pay toggle is painted with the card, but its click handler is attached per-instance
     by budget-pay-livetv.js — a module whose import graph resolves well after this file runs. A tap
     in that window used to hit an unwired button and be silently dropped. Record it so the toggle
     acts on it as soon as it initialises; the module clears the flag when it replays. */
  document.addEventListener(
    "click",
    function (ev) {
      var host =
        ev.target &&
        ev.target.closest &&
        ev.target.closest("[data-budget-pay-toggle]");
      if (!host || host._budgetPayToggleInstance) return;
      host.dataset.ltvBpPendingClick = "1";
      ltvBpDebugLog("early toggle click queued (module not ready)");
    },
    true,
  );

  document.addEventListener("budget-pay:sync-complete", function (ev) {
    ltvBpDebugLog("budget-pay:sync-complete", {
      detail: ev && ev.detail,
      diagnostics: getCoaBudgetPayDiagnostics(
        document.querySelector(
          "article.currently-on-air-card .currently-on-air-card__form",
        ),
      ),
    });
  });

  window.initCoaCardForms = function (formOrScope) {
    if (formOrScope && formOrScope.tagName === "FORM") {
      initCoaForm(formOrScope);
    } else {
      (formOrScope || document)
        .querySelectorAll(".currently-on-air-card__form")
        .forEach(initCoaForm);
    }
  };
  window.initMissedAuctionForms = function () {
    document
      .querySelectorAll(".missed-auction-card")
      .forEach(initMissedAuctionCard);
  };
  /** Used by app.js after buy-all-variants fetch (same filtering as static COA/Missed cards). */
  window._liveTvApplyBuyAllVariantResponse = applyBuyAllVariantResponse;
  /** Used by app.js: Buy All variant load with retry, and the click-time refetch. */
  window._liveTvLoadBuyAllVariants = loadLiveTvBuyAllVariants;
  window._liveTvEnsureBuyAllVariantIds = ensureLiveTvBuyAllVariantIds;
  window._liveTvSortAuctionItemsByDisplaySize = sortAuctionItemsByDisplaySize;
  window._liveTvSortLiveTvSelectOptions = sortLiveTvSelectOptions;
  /** Used by app.js for dynamically rendered missed cards (same bulk POST as COA). */
  window._liveTvPostBuyAllItemsToCart = postBuyAllItemsToCart;
  /** Used by the Currently On Air updater when a refresh cannot keep the previous option. */
  window._liveTvShowSelectionCleared = showLiveTvSelectionCleared;
  /** Tells the updater this file already restores color and size after a card rebuild. */
  window.__ltvThemeOwnsSelectionRestore = true;
})();
